/* WRAPPED IN AN IIFE BECAUSE THE STOCK PAGE NOW LOADS EVERY APP AT ONCE (15/08/2026).
   These were four separate pages, so each could declare `escapeHtml`, `spinnerHtml`,
   `MONTHS` and friends at the top level harmlessly. On one page they share ONE global
   scope, and a second `const escapeHtml` is a SyntaxError that kills the WHOLE script -
   so `customElements.define` never ran and every tab after Stock check sat on its
   spinner for ever. Nothing here is used across files; the wrapper simply stops them
   seeing each other. Same shape as the `money` collision in the sync's inlined modules. */
(function () {
// THE PRE-ORDERS TAB LOADS THIS FILE TOO, for its Create dialog. A second copy on one page
// would register the element twice, which throws - so the second copy stands down.
if (window.customElements && window.customElements.get('deliveries-app')) return;
/**
 * Deliveries - what a delivery note produced, waiting for somebody to tick or cross it.
 *
 * The delivery pipeline reads a note, works out every line - which till product it is, or
 * what a new one should be called, cost, sell for, be filed under and be tagged with - and
 * then STOPS (Jonathan, 15/08/2026). Nothing reaches the till until it is approved here.
 *
 * A tick creates the product and puts the stock on order. A cross rejects the line and
 * asks for no reason. When every line of a note has been decided and none was rejected,
 * its email files itself out of the invoices@ inbox; a note with anything rejected is left
 * alone for somebody to deal with by hand.
 *
 * NAME, DESCRIPTION AND CATEGORY ARE EDITABLE, and what is on screen when you tick is what
 * gets created - not what the automation proposed. Getting a category wrong puts a product
 * on the wrong till buttons and in the wrong reports, silently, so correcting it has to be
 * possible in the moment rather than afterwards.
 */
// A LOOKUP THAT FOUND NOTHING IS RECORDED, NOT REPEATED. The scrape is a paid one, so
// an empty answer is written back as this marker: the page shows a plus button either
// way, and never asks the supplier about that code a second time. A person supplying a
// picture overwrites it, which is the whole point of the button.
//
// THE BACKEND STAMPS IT (11/09/2026): `none|<banks asked>|<day>`, so a miss remembered before a
// bank existed - or long enough ago that a publisher may have filled a page in since - is shown
// by the list as blank and asked about again. The page still SENDS the plain word, and reads
// either form as no picture.
const NO_PICTURE = 'none';

// THE MAIN PICTURE, read through the shared module so every staff page agrees on what the
// marker means. `imageUrl` is still the main one; `imageUrls` is the whole list, main first.
function pictureOf(line) {
  return window.StaffPictures.real(line.imageUrl);
}

function picturesOf(line) {
  return window.StaffPictures.list(line);
}

// A THUMBNAIL-SIZED VERSION WHERE THE HOST CAN GIVE ONE - Shopify's CDN only, display only.
// The rule and its reasons live in `staff-pictures.js`, shared with the other staff pages.
function thumbnailOf(url) {
  return window.StaffPictures.thumbnailOf(url);
}

// The extension the till will be shown, so it has to be the one we mean. A name with no
// extension at all gains one rather than keeping none.
function renamed(name, ext) {
  const base = String(name || 'picture').replace(/\.[^./\\]*$/, '').trim();
  return `${base || 'picture'}.${ext}`;
}

class DeliveriesApp extends HTMLElement {
  connectedCallback() {
    this.url = this.dataset.url;
    this.secret = this.dataset.secret;
    this.who = this.dataset.who || '';
    this.categories = [];

    this.panel = this.querySelector('[data-panel]');
    this.statusLine = this.querySelector('[data-status]');
    // Fetched when the tab is first opened, not with the page (15/10/2026).
    (window.dcWhenShown || ((el, fn) => fn()))(this, () => this.load());
  }

  /**
   * TWO VIEWS OF ONE LIST (15/09/2026). The Deliveries tab shows deliveries; the Pre-Orders
   * tab's Create dialog shows the lines staged from a supplier's ORDER, which carry a `PO-`
   * delivery number - the same rows, the same options, and approving one runs the same
   * creation. Each view shows only its own, so neither can approve the other's lines, and the
   * dialog shows only the supplier chosen at its top.
   */
  preorderMode() {
    return (this.dataset || {}).mode === 'preorders';
  }

  forThisMode(notes) {
    const isPreorder = (note) => String(note.deliveryNo || '').indexOf('PO-') === 0;
    if (!this.preorderMode()) return notes.filter((note) => !isPreorder(note));
    const supplier = String(this.dataset.supplier || '').trim().toLowerCase();
    return notes.filter((note) => isPreorder(note)
      && (!supplier || String(note.supplier || '').trim().toLowerCase() === supplier));
  }

  async call(operation, body, fresh) {
    const response = await (window.dcFetch || fetch)(this.url, {
      method: 'POST',
      cache: fresh ? 'no-store' : 'default',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ secret: this.secret, operation, by: this.who, ...body }),
    });
    // THE FAR SIDE'S OWN WORDS, NOT ITS STATUS CODE. A refusal here carries a reason -
    // "that file is a WebP, and the till accepts only JPEG and PNG" - and throwing on the
    // number alone discarded it before anybody could read it, leaving a person staring at
    // `400` with nothing to act on. The status is kept as the fallback for a refusal that
    // says nothing, and for a body that is not JSON at all.
    if (!response.ok) {
      let why = '';
      try {
        const said = await response.json();
        why = String(said.error || said.message || '').trim();
      } catch (ignored) { /* not JSON - the status is all there is */ }
      throw new Error(why || `The server said no (${response.status}).`);
    }
    return response.json();
  }

  say(message, bad) {
    this.statusLine.hidden = !message;
    this.statusLine.textContent = message || '';
    this.statusLine.classList.toggle('is-bad', Boolean(bad));
  }

  async load() {
    // The section's resting state already shows a spinner saying "Loading deliveries…",
    // so saying it again in the status line put the word on screen twice on first load.
    // A RELOAD - after approving a line, or after a partial save - has replaced that
    // placeholder with the table, so there the status line is the only thing that can say
    // anything and it still should.
    if (!this.querySelector('.deliveries__loading')) this.say('Loading…');
    // The pre-orders are asked for at the same time as the list, not after it (15/10/2026).
    const preOrders = this.loadPreOrders();
    try {
      const data = await this.call('list', {});
      this.categories = data.categories || [];
      // Release dates come with the list since 19/10/2026 - one call, not two (n8n runs are
      // a monthly allowance). A failed read arrives as `releaseDatesError`.
      this.releaseDates = data.releaseDates || null;
      this.releaseDatesError = data.releaseDatesError || (data.releaseDates ? '' : 'the list carried no release dates');
      const notes = this.forThisMode(data.deliveries || []);
      // THE PRE-ORDERS, so a line for one can say what the website is already expecting.
      // Asked of `Pre-Orders Ops` rather than of this page's own backend, which is the
      // path that writes to the till - and that endpoint costs almost nothing when there
      // is nothing flagged. A failure loses the PO mark, never the delivery list.
      this.preOrders = await preOrders;
      this.render(notes);
      this.say('');
      // Not awaited: the table is usable immediately and the pictures land as they are
      // found. A scrape takes a few seconds per product and nobody should be watching a
      // spinner for artwork they may not even need to change.
      this.fillMissingPictures(notes);
    } catch (err) {
      this.say(`Could not load deliveries: ${err.message}`, true);
    }
  }

  /**
   * ONE SECTION PER SUPPLIER, NOT ONE PER DELIVERY NOTE (Jonathan, 03/09/2026): *"we only
   * want one section for everything from one supplier. We don't need three sections that
   * each say Asmodee."*
   *
   * The backend answers with a note per document, which is what the till and the filing
   * care about. Nobody standing at the tablet does: they have a pile of boxes from Asmodee,
   * and three identical headings only made them scroll and lose their place. Since the
   * heading stopped showing the note number on 24/08 the three were literally
   * indistinguishable.
   *
   * EVERY DELIVERY NUMBER IS KEPT, on `deliveryNos`. Approving still happens a note at a
   * time - the backend selects rows BY note and files the email BY note - so the merge is
   * a way of showing the work, never a way of doing it.
   */
  bySupplier(notes) {
    const groups = new Map();
    for (const note of notes || []) {
      const supplier = note.supplier || 'Delivery';
      const held = groups.get(supplier);
      if (!held) {
        groups.set(supplier, Object.assign({}, note, {
          deliveryNos: [note.deliveryNo], lines: [...(note.lines || [])],
        }));
        continue;
      }
      if (!held.deliveryNos.includes(note.deliveryNo)) held.deliveryNos.push(note.deliveryNo);
      held.lines.push(...(note.lines || []));
    }
    return [...groups.values()];
  }

  render(deliveries) {
    this.panel.innerHTML = '';
    if (!deliveries.length) {
      this.panel.innerHTML =
        this.preorderMode()
          ? '<p class="deliveries__empty">Nothing ordered from this supplier is waiting to be created.</p>'
          : '<p class="deliveries__empty">No deliveries are waiting to be approved.</p>';
      return;
    }
    this.bySupplier(deliveries).forEach((note) => this.panel.appendChild(this.note(note)));
    // A reload rebuilds every button switched on, so a supplier still being approved in
    // full must have its own buttons put back off.
    this.setGlobalControls();
  }

  /**
   * The lines of one note, with repeats of the same product amalgamated.
   *
   * A NOTE CAN LIST ONE PRODUCT SEVERAL TIMES - note 1781951 carries BCL2863367 twice,
   * 24 boxes and 156 - and they are one thing to approve, not two (Jonathan, 15/08/2026).
   * The till write already merges them into a single movement, so two rows here would
   * have the page disagreeing with what it is about to do.
   *
   * Every underlying review row is kept on the group, because approving has to mark all
   * of them and not just the one the reader can see.
   */
  amalgamate(lines) {
    const groups = new Map();
    for (const line of lines) {
      // Restocks group on the till product; a new product has none yet, so on the code.
      //
      // AND ON THE SKU SINCE 12/09/2026, OR A SPLIT UNDOES ITSELF ON SCREEN. Every piece of
      // a split keeps the supplier's code - they came from one line - so on the reload that
      // follows a split the pieces met here and collapsed back into ONE line with their
      // units added up. The feature would have looked like it had done nothing.
      //
      // THE SKU AND NOT THE NAME, which is the same choice `Plan Decision` makes for the
      // merge it does at approval, and for the same reason: a name is edited on this page
      // all day, so keying on it would split two GENUINE repeats into two till products the
      // moment somebody tidied one of them - the dangerous direction. A SKU is not editable
      // here, so it can differ only by staging or by a deliberate split.
      //
      // Genuine repeats are untouched: one product staged twice carries the same code and
      // the same SKU, blank or not, so it still merges.
      const key = line.kind + ':' + (line.tillId || line.code) + ':' + (line.sku || '');
      const held = groups.get(key);
      if (!held) {
        groups.set(key, Object.assign({}, line, {
          units: Number(line.units) || 0, rowIds: [line.rowId],
        }));
        continue;
      }
      held.units += Number(line.units) || 0;
      held.rowIds.push(line.rowId);
    }
    return [...groups.values()];
  }

  /** One delivery note: its own box, its own Approve all, its own table. */
  note(note) {
    const box = document.createElement('section');
    box.className = 'deliveries__note';
    // EVERY note number the section covers, not just the first - the box is one supplier
    // now, and a single number here would name one of three deliveries and look definitive.
    box.dataset.deliveryNo = (note.deliveryNos || [note.deliveryNo]).join(',');

    const head = document.createElement('div');
    head.className = 'deliveries__note-head';
    const title = document.createElement('h3');
    title.className = 'deliveries__note-title';
    // THE SUPPLIER, AND NOT THE DELIVERY NUMBER (Jonathan, 24/08/2026). It means nothing to
    // whoever is standing at the tablet with the boxes - the note number is a supplier's
    // filing reference, and it made the heading read like a code rather than a name. It is
    // still on the box as `data-delivery-no`, which is what Approve all sends, so nothing
    // that acts on a note has lost it.
    title.textContent = note.supplier || 'Delivery';
    head.appendChild(title);

    // AT THE TOP, as he asked. A note is usually right, so the common case is one press.
    // NOT ON THE ORDERED-FROM-SUPPLIERS LIST (Jonathan, 15/09/2026): a supplier order is a
    // list to pick from, one product at a time, not a delivery to accept whole.
    if (!this.preorderMode()) {
      const all = document.createElement('button');
      all.type = 'button';
      all.className = 'deliveries__approve-all';
      all.textContent = 'Approve all';
      all.addEventListener('click', () => this.approveAll(box, note));
      head.appendChild(all);
    }
    box.appendChild(head);

    const pending = this.amalgamate((note.lines || []).filter((l) => l.status === 'pending'));

    const table = document.createElement('table');
    table.className = 'deliveries__table';
    // PRICE, NOT RRP (Jonathan, 15/08/2026). RRP is the supplier's suggestion and on a
    // restock it was DERIVED from Asmodee's own sheet rather than read from the till - so
    // the column stated a retail price the shop had never agreed to, next to a product it
    // has been selling for months at its own. What matters when approving a delivery is
    // what the shop charges. The RRP is still carried on the review row, because creating
    // a product prices off it; it is simply not what this column shows.
    //
    // The headings are one word each where one word will do: "Sale price" and "Till
    // category" said out loud what the reader can already see from the figures under them,
    // and the qualifier is only ever needed to distinguish them from a rival this page
    // does not show. There is one price here and one category.
    //
    // NAME AND DESCRIPTION ARE THE EXCEPTION, AND THEY EARN IT (Jonathan, 03/09/2026).
    // Those two ARE a pair with a rival, and which is which decides what a customer sees:
    // the till's name is the shop floor's, and the description is what the website listing
    // is called. Titled `Name` and `Description` the second read like a footnote nobody
    // had to get right; titled `Website name` it is plainly the thing being decided.
    // PICTURE FIRST (Jonathan, 24/08/2026). The artwork is agreed at the same moment as
    // the product, so it belongs where the eye lands before it starts reading - and a
    // column of thumbnails makes the ones with NO picture obvious at a glance, which is
    // the whole point: those are the products that would go on the website invisible.
    // RELEASE, ON THE PRE-ORDER VIEW ONLY (Jonathan, 02/10/2026): what has been ordered and
    // not delivered is waiting on a release date, and that is the first thing asked about it.
    const headings = ['Image', 'Type', 'Till name', 'Website name', 'Qty', 'Cost', 'Price',
                      ...(this.preorderMode() ? ['Release'] : []), 'Category', ''];
    // Centred: the numbers, and the Image and Type headings over their centred contents.
    const numeric = { Image: true, Type: true, Qty: true, Cost: true, Price: true, Release: true };
    const thead = document.createElement('thead');
    const hrow = document.createElement('tr');
    headings.forEach((h) => {
      const th = document.createElement('th');
      th.textContent = h;
      if (numeric[h]) th.className = 'num';
      // Same padding as the picture cells beneath, so the heading centres over the pictures.
      if (h === 'Image') th.classList.add('deliveries__cell-image');
      if (h === '') th.className = 'actions';
      hrow.appendChild(th);
    });
    thead.appendChild(hrow);
    table.appendChild(thead);

    const body = document.createElement('tbody');
    pending.forEach((line) => {
      const own = this.line(line);
      body.appendChild(own);
      // THE LOOSE BOOSTER SITS UNDER ITS BOX (Jonathan, 22/08/2026), on a row of its own
      // with no tick. It is not a second decision: approving the box creates both, and
      // giving it its own controls would let the two get out of step - a booster approved
      // without its master has nowhere for its stock to live, and a box approved without
      // its booster is the half-made set this whole change exists to end.
      const extra = this.boosterRow(line);
      if (extra) body.appendChild(extra);
      // THE PREVIOUS SET'S FIGURES GO LAST, after the booster, so "the row straight after a
      // box is its booster" stays true for everything that relies on it (`boosterRowOf`).
      // Not a `.deliveries__row`, so nothing that walks the lines ever mistakes it for one;
      // it greys with its line in `retireRow`.
      const prior = this.priorRow(line);
      if (prior) {
        body.appendChild(prior);
        own.priorRow = prior;
      }
    });
    table.appendChild(body);
    box.appendChild(table);
    if (this.preorderMode()) this.loadReleaseDates(table);
    return box;
  }

  // THE RELEASE DATE COMES WITH THE LIST (`Deliveries Ops` -> `releaseDates`, the same rule
  // `Pay On Arrival Ops` uses), from Asmodee's own order forms as they stand tonight, so a
  // date Asmodee move is moved here too. A cell with no date says so with a dash; a failed
  // read says nothing at all rather than a dash, which would read as "Asmodee publish no date".
  releaseCell(line) {
    const cell = document.createElement('td');
    cell.className = 'num deliveries__cell-release';
    if (line && line.code) cell.dataset.releaseCode = line.code;
    return cell;
  }

  loadReleaseDates(table) {
    const cells = [...table.querySelectorAll('[data-release-code]')];
    if (!cells.length) return;
    const dates = this.releaseDates;
    if (!dates) {
      cells.forEach((cell) => { cell.title = `The release date could not be read: ${this.releaseDatesError}`; });
      return;
    }
    cells.forEach((cell) => {
      const iso = dates[cell.dataset.releaseCode];
      cell.textContent = iso ? iso.split('-').reverse().join('/') : '—';
      if (!iso) cell.title = 'Asmodee publish no release date for this product.';
    });
  }

  // THE PICTURE THE PRODUCT WILL BE CREATED WITH, or the offer to supply one.
  //
  // A RESTOCK SHOWS THE TILL'S OWN PICTURES (Jonathan, 31/08/2026) - the quickest confirmation
  // that the line being ticked is the product the box actually contains. The URLs come from
  // the till's own record rather than the review row, resolved when the page is read (see
  // `fromTill` in Build List), so they are the pictures the product has NOW.
  //
  // A RESTOCK WITH NO PICTURE GETS THE PLUS (Jonathan, 07/09/2026), and `setRestockImage` puts
  // the picture straight onto the till product, where a create writes it onto the review row
  // for the approval to carry through.
  //
  // MORE THAN ONE PICTURE (Jonathan, 12/09/2026). The cell shows the main picture with a count,
  // and a tap opens them all side by side. THIS PAGE IS WHERE THE MAIN ONE IS CHOSEN - it is
  // where a product's details are set, and once it reaches Stock processing they are fixed -
  // and it is wanted on restocks as well as new products. Tapping a picture makes it the main
  // one; closing the gallery any other way changes nothing.
  //
  // A restock's pictures are still not replaceable from here, only reordered: a photograph the
  // product already has was chosen by somebody.
  imageCell(line, isNew) {
    const cell = document.createElement('td');
    cell.className = 'deliveries__cell-image';
    const pictures = picturesOf(line);
    if (!pictures.length) return this.addPictureButton(line, cell);
    const main = pictures[0];

    const holder = document.createElement('span');
    holder.className = 'staff-pictures';
    const thumb = document.createElement('img');
    thumb.className = 'deliveries__thumb';
    // No referrer, or ABGee's site refuses the picture (see staff-pictures.js).
    thumb.referrerPolicy = 'no-referrer';
    thumb.src = thumbnailOf(main);
    thumb.alt = '';
    // NOT `lazy`. A lazily-loaded image is not fetched until the browser has worked out where
    // it lands, which happens AFTER the first paint - so every picture arrived a beat late and
    // the row appeared with an empty frame (10/09/2026). A delivery is a handful of rows and
    // all of them are above the fold.
    const several = pictures.length > 1;
    // NO HOVER TEXT (Jonathan, 20/10/2026): it sat over the large preview of the picture.
    // A LARGE VERSION ON HOVER for whoever is at a desk, positioned against the WINDOW rather
    // than the cell, because the table scrolls sideways on a narrow screen.
    thumb.addEventListener('mouseenter', () => window.StaffPictures.showPreview(thumb, main));
    thumb.addEventListener('mouseleave', () => window.StaffPictures.hidePreview());
    thumb.addEventListener('click', () => this.openPictures(line, cell, isNew));
    holder.appendChild(thumb);
    if (several) {
      const count = document.createElement('span');
      count.className = 'staff-pictures__count';
      count.textContent = String(pictures.length);
      count.setAttribute('aria-label', `${pictures.length} pictures`);
      holder.appendChild(count);
    }
    cell.appendChild(holder);
    return cell;
  }

  /**
   * Every picture on the line, with the main one chosen by a tap.
   *
   * A NEW PRODUCT WITH ONE PICTURE keeps what a click on it always did - open the file picker to
   * replace it - because there is nothing to choose between, and a wrong photograph is worth
   * changing before it reaches the website. With several, the gallery opens and adding or
   * replacing is offered inside it.
   *
   * THE LOOSE BOOSTER STAYS SINGLE. Its picture rides on its box's review row in a column of its
   * own, and a list there is not part of this change.
   */
  openPictures(line, cell, isNew) {
    window.StaffPictures.hidePreview();
    const pictures = picturesOf(line);
    const child = line.target === 'child';
    // THE LOOSE BOOSTER KEEPS ITS SINGLE PICTURE AND ITS OLD CLICK-TO-REPLACE. Everything else
    // opens the gallery, even with one picture, because that is where its cross is.
    if (isNew && child) return this.askForImage(line, cell, 'replace');

    // THE CELL IS HELD BY REFERENCE, because a choice redraws it. Several choices can be made
    // in one open gallery, and each must redraw the cell that is on the page NOW - holding the
    // first one would leave the row showing whichever picture was chosen first (found live,
    // 12/09/2026: choose one, choose back, and the row still showed the middle choice).
    const ref = { cell };
    // ONE PLUS, NOTHING ELSE (Jonathan, 12/09/2026): a picture is added, never the whole set
    // replaced from here.
    const actions = isNew ? [
      { label: '+', ariaLabel: 'Add a picture', plus: true,
        run: () => this.askForImage(line, ref.cell, 'add') },
    ] : [];
    return window.StaffPictures.open(pictures, {
      title: line.name || '',
      actions,
      onChoose: child ? null : (url) => this.chooseMainPicture(line, ref, isNew, url),
      onRemove: child ? null : (url) => this.removePicture(line, ref, isNew, url),
    });
  }

  /**
   * REMOVE ONE PICTURE (Jonathan, 12/09/2026: a cross on each picture), and believe the far side.
   *
   * The backend decides the route the way it does for choosing a main picture: on a new product,
   * or a restock whose pictures are still only on its delivery line, the line's list loses it; on
   * a restock whose till product holds pictures it is deleted from the till. Removing the last one
   * leaves the line marked as having none, so the lookup does not put it straight back.
   */
  async removePicture(line, ref, isNew, url) {
    const answer = await this.call('removeImage', {
      rowId: line.rowId, tillId: isNew ? undefined : line.tillId, imageUrl: url,
    });
    if (!answer || !('imageUrl' in answer)) throw new Error('the server did not confirm the change');
    const now = window.StaffPictures.list(answer);
    if (now.includes(url)) throw new Error('the picture is still there');
    line.imageUrl = now[0] || window.StaffPictures.NO_PICTURE;
    line.imageUrls = now;
    const next = this.imageCell(line, isNew);
    ref.cell.replaceWith(next);
    ref.cell = next;
    return now;
  }

  /**
   * MAKE ONE PICTURE THE MAIN ONE, and believe the far side about what it now holds.
   *
   * On a new product that is only an order on the review row - which picture is registered
   * first. On a restock whose till product holds pictures it changes the till: the till cannot
   * promote a picture, so the backend deletes the current main and registers the chosen one as
   * main, putting the old one back if the second step fails. Either way the answer is the list
   * as it now stands, and that is what the line is redrawn from.
   */
  async chooseMainPicture(line, ref, isNew, url) {
    const answer = await this.call('setMainImage', {
      rowId: line.rowId, tillId: isNew ? undefined : line.tillId, imageUrl: url,
    });
    const now = window.StaffPictures.list(answer || {});
    // AN ANSWER THAT DOES NOT CONFIRM THE CHOICE IS NOT BELIEVED. Without this a backend that
    // knows no such operation, and answers with nothing, would look as though it had done it.
    //
    // ON A RESTOCK WHOSE PICTURES ARE ON THE TILL THE MAIN KEEPS ITS ADDRESS (14/09/2026): the
    // till files every main picture at the product's one fixed address, so the chosen picture
    // comes back under the address the OLD main had, and its own address is gone from the list.
    // That is a confirmation only when the server says `ok` - and the address now holds new
    // bytes, so the browser is told to draw it afresh.
    const sameAddress = now[0] === url;
    const movedToMain = !!(answer && answer.ok === true) && now.length > 0 && !now.includes(url);
    if (!sameAddress && !movedToMain) throw new Error('the server did not confirm the change');
    if (movedToMain) window.StaffPictures.changed(now[0]);
    line.imageUrl = now[0];
    line.imageUrls = now;
    const next = this.imageCell(line, isNew);
    ref.cell.replaceWith(next);
    ref.cell = next;
    return now;
  }

  // NO PICTURE FOUND. A plus rather than a blank: a blank reads as a column that failed to
  // load, where a plus says plainly that this product has none and somebody can fix it
  // here. On a create, without one the listing is created hidden, which nobody sees happen;
  // on a restock, the listing is ALREADY hidden for the same reason and has been since it
  // was made, which is worse - nothing has ever offered a way to put that right from here.
  //
  // ONE BUTTON FOR BOTH KINDS, deliberately. It was written twice for about a minute and
  // the two copies already disagreed about the tooltip.
  addPictureButton(line, cell) {
    const plus = document.createElement('button');
    plus.type = 'button';
    plus.className = 'deliveries__thumb-add';
    plus.textContent = '+';
    plus.title = line.target === 'child'
      ? 'The loose booster has no picture of its own - click to upload one'
      : line.kind === 'create'
        ? 'No picture found - click to upload one'
        : 'This product has no picture - click to put one on it';
    plus.setAttribute('aria-label', `Add a picture for ${line.name || 'this product'}`);
    plus.addEventListener('click', () => this.askForImage(line, cell));
    cell.appendChild(plus);
    return cell;
  }

  // SUPPLYING A PICTURE BY HAND: THE FILE PICKER, AND NOTHING ELSE.
  //
  // [[Jonathan Hicks]], 08/09/2026: *"We just want the file picker. We don't need the
  // paste link route."*
  //
  // THIS USED TO CHAIN A SECOND DIALOG ONTO CANCEL. Cancelling the picker was read as
  // "he wants the other way" and opened a `prompt()` asking for an image address. He
  // reported it as odd, and he was right: a button that says Cancel must cancel. Two
  // native dialogs in a row also read as a fault rather than a choice, which is how it
  // was reported.
  //
  // The paste-a-link route is GONE rather than moved. With it goes the only way to
  // REMOVE a picture by hand - the old prompt cleared a create's picture on an empty
  // answer - so if that is ever wanted again it needs its own control, deliberately,
  // and not a dialog hidden behind the cancellation of another one.
  //
  // `mode` says what an uploaded picture does to a new product's LIST (12/09/2026): `add` puts
  // it in front as the main one and keeps the rest, `replace` leaves it as the only one - which
  // is what a click on a single wrong photograph has always meant.
  async askForImage(line, cell, mode) {
    window.StaffPictures.hidePreview();
    const file = await this.pickFile();
    if (!file) return null;
    return this.uploadPicture(line, cell, file, mode || 'replace');
  }

  pickFile() {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/jpeg,image/png,image/webp,image/gif';
      // CANCELLING IS THE INPUT'S OWN `cancel` EVENT, not a guess from window focus.
      //
      // It used to settle on the window regaining focus, 300ms later, because a cancelled
      // picker fires nothing in some browsers and the paste fallback had to be offered.
      // That heuristic was wrong in a way nobody had hit yet: the window also regains
      // focus when you switch to another app and back, so alt-tabbing WHILE THE PICKER IS
      // STILL OPEN settled the promise - and a file chosen afterwards was then silently
      // discarded, because `done` had already fired.
      //
      // With the paste route gone there is nothing to offer on cancel, so the heuristic
      // has no job left. `change` carries the file; `cancel` says there is none. A browser
      // that fires neither simply never resolves, which now costs nothing at all - the
      // button did nothing either way.
      let settled = false;
      const done = (value) => { if (!settled) { settled = true; resolve(value); } };
      input.addEventListener('change', () => done(input.files && input.files[0]));
      input.addEventListener('cancel', () => done(null));
      input.click();
    });
  }

  async uploadPicture(line, cell, file, mode) {
    try {
      this.say(`Uploading ${file.name}…`);
      const picture = await this.asTillFormat(file);
      // SHOW IT NOW, FROM THE FILE ALREADY IN THE BROWSER (Jonathan, 10/09/2026: pictures
      // "take 5 or 6 seconds to actually appear on the page once I've uploaded the file").
      // Nothing about Shopify's round trip is needed to draw a picture the person just
      // chose - we are holding the bytes. This paints in a frame and the real address
      // replaces it silently when it arrives.
      //
      // IT IS UNDONE ON FAILURE, WHICH IS THE WHOLE CARE OF IT. A picture left on screen
      // after a failed upload is a false claim that the product has one, and it is exactly
      // the shape of fault this project keeps finding - so the catch below puts the cell
      // back. He guessed lazy loading; that was yesterday's fault on first paint and is
      // already fixed. This one is the poll below.
      const preview = this.showLocalPicture(cell, picture.blob);
      const fileData = await this.base64(picture.blob);
      // `target` says WHICH picture on the row: blank for the line itself, `child` for the
      // loose booster beneath a box. It rides on every poll too, because the poll is what
      // stores the finished address and it has to land in the booster's column, never the
      // box's.
      const started = await this.call('uploadImage', {
        rowId: line.rowId, fileName: picture.name, fileType: picture.type, fileData,
        target: line.target || undefined,
      });
      let url = started.imageUrl || '';
      // SHOPIFY FINISHES THE UPLOAD AFTER IT ACCEPTS IT, so the address does not exist
      // for a second or two. Asked for rather than waited on inside the request: a
      // webhook that parks itself is a browser that times out, which it did twice.
      //
      // ASK FIRST, THEN WAIT - the other half of the delay he reported. This used to sleep
      // 1500ms BEFORE its first question, so a file Shopify had already finished still cost
      // a second and a half, and two or three were common. The waits now start short and
      // grow, which asks more often early (when it is most likely to be ready) and no more
      // often late. Same patience in total: 8 tries, ~10s, against the old 12s.
      const waits = [0, 250, 500, 750, 1000, 1500, 2000, 4000];
      for (let tries = 0; !url && tries < waits.length; tries += 1) {
        if (waits[tries]) await new Promise((r) => setTimeout(r, waits[tries]));
        const ready = await this.call('imageReady', {
          rowId: line.rowId, fileId: started.fileId, target: line.target || undefined,
        });
        url = ready.imageUrl || '';
      }
      if (!url) throw new Error('Shopify is still processing it - try again shortly.');
      // The real address exists now, so the handle to the local file can go. Done here
      // rather than in a `finally`, because the failure path needs it alive long enough to
      // put the cell back.
      if (preview) { try { URL.revokeObjectURL(preview); } catch (ignored) { /* nothing */ } }
      // A RESTOCK IS NOT FINISHED HERE. The upload has put the file somewhere with an
      // address; on a create that address IS the answer, because approving the row is what
      // later carries it to the till. A restock has no approval to wait for - the product
      // exists - so the address is handed on to be registered against it now.
      if (line.kind !== 'create') return this.savePicture(line, cell, url, 'setRestockImage', [url]);
      // THE BOOSTER'S OWN PICTURE IS STORED BY THE POLL ABOVE, into its own column, and is a
      // single picture - so nothing more is written for it.
      if (line.target === 'child') {
        line.imageUrl = url;
        cell.replaceWith(this.imageCell(line, true));
        this.say('Picture uploaded.');
        return url;
      }
      // THE LIST IS WRITTEN HERE, not left to the poll: the poll only knows the one address it
      // was waiting for, and the review row must never name a main picture its list disagrees
      // with.
      const kept = mode === 'add' ? picturesOf(line).filter((u) => u !== url) : [];
      return this.savePicture(line, cell, url, 'setImage', [url, ...kept]);
    } catch (error) {
      // TAKE THE OPTIMISTIC PICTURE BACK DOWN. Leaving it is a false claim that the product
      // has a picture, and it is the exact shape this project keeps being bitten by: the
      // screen says done and nothing was saved. The cell goes back to whatever it held
      // before - a thumbnail if it had one, the plus if it did not.
      this.undoLocalPicture(line, cell);
      this.say(`Could not upload that picture: ${error.message}`, true);
      return null;
    }
  }

  /**
   * PAINT THE FILE THE PERSON JUST CHOSE, BEFORE ANYTHING IS UPLOADED.
   *
   * JONATHAN, 10/09/2026: pictures "take 5 or 6 seconds to actually appear on the page once
   * I've uploaded the file". He guessed lazy loading; that was the 09/09 fault on FIRST
   * paint and is already fixed. This is a different one, and there is no reason to wait at
   * all - the bytes are in the browser before the request is even sent.
   *
   * AN OBJECT URL, NOT A base64 READ. The upload needs base64 and that read costs real time
   * on a megabyte file; `URL.createObjectURL` is a handle, so it is free and the picture is
   * on screen while the encoding happens.
   *
   * IT IS REVOKED WHEN THE REAL ADDRESS ARRIVES, or the page holds the whole file in memory
   * for as long as it is open - a delivery of thirty lines is thirty files.
   */
  showLocalPicture(cell, blob) {
    let handle = '';
    try {
      handle = URL.createObjectURL(blob);
    } catch (ignored) {
      // Never worth failing an upload over: the picture simply appears when it lands, which
      // is what happened before this existed.
      return null;
    }
    const img = document.createElement('img');
    img.className = 'deliveries__thumb deliveries__thumb--pending';
    img.src = handle;
    img.alt = '';
    img.title = 'Uploading…';
    cell.textContent = '';
    cell.appendChild(img);
    return handle;
  }

  /** Put the cell back to what it held before an upload that failed. */
  undoLocalPicture(line, cell) {
    if (!cell || !cell.parentNode) return;
    cell.replaceWith(this.imageCell(line, line.kind === 'create'));
  }

  // THE TILL TAKES ONLY .jpg AND .png, AND IT JUDGES ON THE FILE NAME. A picture saved
  // out of a browser is very often a .webp, so on 03/09 one reached EposNow, came back
  // `Invalid extension. Only .jpg, .png allowed.`, and the refusal was swallowed - the
  // product was created with no picture and its listing went live as a pictureless draft
  // with nobody told. Converting HERE means nothing the till cannot take is ever sent,
  // and staff can go on saving whatever format a supplier's site happens to serve.
  async asTillFormat(file) {
    const type = String(file.type || '').toLowerCase();
    if (type === 'image/jpeg' || type === 'image/png') {
      // Already acceptable - but the NAME still has to agree with the bytes, because the
      // name is the only thing the till looks at. A PNG saved as `art.webp` is exactly
      // the case that bit us.
      const ext = type === 'image/png' ? 'png' : 'jpg';
      return { blob: file, name: renamed(file.name, ext), type };
    }

    const bitmap = await this.decode(file);
    // An ImageBitmap reports `width`; the <img> fallback's own `width` is its laid-out
    // size, which is zero for an element never put on the page, so ask for the real one.
    const width = bitmap.naturalWidth || bitmap.width;
    const height = bitmap.naturalHeight || bitmap.height;
    if (!width || !height) throw new Error('that file is not an image this browser can read');
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    // A transparent source drawn straight into a JPEG comes out with a black background,
    // so the transparency is flattened onto white first.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0);
    if (bitmap.close) bitmap.close();

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (made) => (made ? resolve(made) : reject(new Error('it could not be converted to a JPEG'))),
        'image/jpeg',
        0.92,
      );
    });
    return { blob, name: renamed(file.name, 'jpg'), type: 'image/jpeg' };
  }

  // `createImageBitmap` is the direct route; the older tablets in the shop fall back to
  // letting an <img> do the decoding.
  decode(file) {
    if (window.createImageBitmap) return window.createImageBitmap(file);
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => { URL.revokeObjectURL(url); resolve(image); };
      image.onerror = () => { URL.revokeObjectURL(url); reject(new Error('that file is not an image this browser can read')); };
      image.src = url;
    });
  }

  base64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      // The data URL carries a `data:...;base64,` preamble the server has no use for.
      reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
      reader.onerror = () => reject(new Error('that file could not be read'));
      reader.readAsDataURL(file);
    });
  }

  async savePicture(line, cell, url, operation, urls) {
    try {
      this.say('Saving the picture…');
      const saved = await this.call(operation, {
        rowId: line.rowId, tillId: line.tillId, imageUrl: url, code: line.code,
        imageUrls: urls && urls.length ? urls : undefined,
      });
      // AN OLDER ANSWER CARRIES ONLY THE ONE ADDRESS, so the list that was sent is kept.
      line.imageUrl = saved.imageUrl || '';
      line.imageUrls = Array.isArray(saved.imageUrls) ? saved.imageUrls
        : (line.imageUrl && urls ? urls : []);
      // RE-RENDERED AS THE KIND IT ACTUALLY IS. This passed a literal `true` while only a
      // create could reach it; a restock redrawn as a create would gain a click-to-replace
      // thumbnail that writes to a review row nothing will ever approve.
      cell.replaceWith(this.imageCell(line, line.kind === 'create'));
      this.say(line.imageUrl ? 'Picture saved.' : 'Picture removed.');
      return line.imageUrl;
    } catch (error) {
      this.say(`Could not save that picture: ${error.message}`, true);
      return null;
    }
  }

  // FIND THE MISSING PICTURES, ONCE. Anything already answered - a real address, or the
  // marker saying we looked and found none - is skipped, so the paid scrape happens once
  // per product for ever rather than once per visit to this page.
  //
  // It runs AFTER the table is drawn, so the page is usable while it works: the
  // thumbnails appear where they are found, and the rest keep their plus button.
  async fillMissingPictures(notes) {
    const wanted = [];
    (notes || []).forEach((note) => (note.lines || []).forEach((line) => {
      // A RESTOCK IS LOOKED UP TOO (Jonathan, 03/09/2026): *"We need pictures for restocks
      // as well. It looks like we're not even trying to get those."* And we were not - this
      // read `kind !== 'create'` and skipped every one.
      //
      // THE TILL'S OWN PICTURE STILL WINS, and nothing here can override it: `Build List`
      // resolves a restock's `imageUrl` from the till product first, so a line only reaches
      // this hunt when the till holds none. That is his order exactly - take the one we
      // have, and go looking only when we have not got one.
      if (String(line.imageUrl || '').trim()) return;
      if (!line.code) return;
      if (wanted.some((w) => w.code === line.code)) return;
      // THE BARCODE, WITHOUT WHICH THE heo BANK CANNOT BE ASKED AT ALL (03/09/2026).
      // heo is joined on the barcode and nothing else, so a lookup sent without one is
      // silently skipped - `Any heo Lookups?` produced zero items on every real page load,
      // and the general-purpose fallback added on 31/08 had therefore never once run from
      // this page. The backend has carried the field since that day; the browser simply
      // never filled it in. Third time this exact shape has bitten: a field the far side
      // reads, that nothing supplies, fails as silence.
      // THE SUPPLIER IS ON THE NOTE, NOT THE LINE. It promotes whoever sent the boxes to
      // the front of the picture banks, which is his 31/08 rule - reading `line.supplier`
      // would have sent an empty string and quietly lost that ordering.
      // A BOX WITH A LOOSE BOOSTER UNDER IT (03/10/2026): the supplier's pictures are sorted
      // into box shots for the box and a pack shot for the booster, server-side.
      wanted.push({ code: line.code, name: line.name || '',
                    barcode: line.barcode || '', supplier: note.supplier || '',
                    boosterBox: !!line.child });
    }));
    if (!wanted.length) return;

    // ASKED IN SMALL BATCHES, because one big ask outruns the gateway (08/09/2026).
    //
    // The supplier scrape costs TWO rate-limited calls per product and the backend spaces
    // them five seconds apart, so thirteen products is well over two minutes - and the
    // webhook is answered through Cloudflare, which gives up at about a hundred seconds
    // with HTTP 524. The work completes server-side and the ANSWER is thrown away, so
    // every picture found is lost and the frames stay empty. Same shape as "approve all"
    // on a large delivery: it looks like a failure and is not.
    //
    // Four at a time is ~40 seconds, comfortably inside that, and the pictures appear a
    // handful at a time while the rest are still being looked up rather than all at the
    // end. Anything a batch could not answer is left for the next visit exactly as before.
    const BATCH = 4;
    let failed = 0;
    for (let start = 0; start < wanted.length; start += BATCH) {
      const batch = wanted.slice(start, start + BATCH);
      await this.fetchPictureBatch(notes, batch)
        .then((n) => { failed += n; })
        .catch(() => { failed += Math.min(BATCH, wanted.length - start); });
      // ONLY THE PICTURE CELLS ARE SWAPPED, never the whole list (Jonathan, 15/09/2026).
      // This used to call `render(notes)`, which rebuilds every row from what the server last
      // sent - so a name, price or section somebody had typed while a batch was out was
      // silently thrown away, and approval then created the product under the OLD name. It
      // happened to Codex: Space Marines (HB), while the three kits beside it were being looked
      // up. A lookup takes ~40 seconds a batch, which is exactly long enough to be typing.
      this.refreshPictureCells(notes, batch);
    }
    if (failed) {
      this.say(`Could not look up ${failed} picture${failed === 1 ? '' : 's'} just now `
               + '(the supplier lookup is rate-limited). Reload in a minute to try again.',
               false);
    }
  }

  /**
   * Put the pictures a batch found onto their rows, touching nothing else on the row.
   *
   * The row is found by its review row ids, which is how every decision on this page
   * addresses it. A booster's own row is skipped: the lookup only ever fills the box's
   * picture, and the booster keeps its own cell.
   */
  refreshPictureCells(notes, batch) {
    const codes = new Set(batch.map((w) => w.code));
    const rows = [...this.querySelectorAll('.deliveries__row')]
      .filter((row) => !row.classList.contains('deliveries__row--child'));
    (notes || []).flatMap((n) => n.lines || [])
      .filter((line) => codes.has(line.code) && String(line.imageUrl || '').trim())
      .forEach((line) => {
        const ids = (line.rowIds || [line.rowId]).map(String).join(',');
        const row = rows.find((r) => r.dataset.rowIds === ids);
        // A row already decided keeps what it showed when it was decided.
        if (!row || row.classList.contains('is-approved') || row.classList.contains('is-rejected')) return;
        const cell = row.querySelector(':scope > .deliveries__cell-image');
        if (cell) cell.replaceWith(this.imageCell(line, line.kind === 'create'));
        // ITS BOOSTER'S CELL TOO, which sits on the row straight after the box's.
        const next = row.nextElementSibling;
        if (line.child && next && next.classList.contains('deliveries__row--child')) {
          const childCell = next.querySelector(':scope > .deliveries__cell-image');
          if (childCell) childCell.replaceWith(this.imageCell(this.boosterSlot(line), true));
        }
      });
  }

  /**
   * One batch of picture lookups, written back one row at a time.
   *
   * Returns how many the far side could not look up - a REFUSAL, which must never be
   * remembered as "this product has no photograph". Anything else is an answer, including
   * a genuine absence, and is stored so the next visit does not pay for it again.
   */
  async fetchPictureBatch(notes, wanted) {
    const found = await this.call('findImages', { codes: wanted });
    let failed = 0;
    for (const hit of found.images || []) {
      // A LOOKUP THAT COULD NOT HAPPEN IS NEVER REMEMBERED (Jonathan asked why there
      // were no pictures, 03/09/2026). FireCrawl answers 429 once the minute's
      // allowance is spent, and writing the marker for that records "this product has
      // no photograph" against one that has a perfectly good one - for ever, because
      // the marker is exactly what stops it being asked again. Eight real products were
      // poisoned in one page load. Left alone, the next visit simply asks again.
      if (hit.lookupFailed) { failed += 1; continue; }
      // The marker goes in for a genuine miss, so the next visit does not pay for the
      // same answer. Saved one at a time: a failure on one code must not lose the others.
      const url = hit.imageUrl || NO_PICTURE;
      const lines = (notes || []).flatMap((n) => n.lines || [])
        // Restocks included, for the same reason they are now asked about at all. The
        // write goes to the review row; putting it on the TILL is what approving does,
        // through the upload chain a created product already uses.
        .filter((l) => l.code === hit.code && !String(l.imageUrl || '').trim());
      for (const line of lines) {
        try {
          // EVERY PICTURE THE SUPPLIER PUBLISHES, main first (12/09/2026). Sent only when the
          // lookup returned a list, so an answer from a single-picture bank is written exactly
          // as it always was.
          const urls = hit.imageUrl && Array.isArray(hit.imageUrls) && hit.imageUrls.length
            ? hit.imageUrls : null;
          await this.call('setImage', urls
            ? { rowId: line.rowId, imageUrl: url, imageUrls: urls, code: line.code }
            : { rowId: line.rowId, imageUrl: url, code: line.code });
          line.imageUrl = url;
          line.imageUrls = urls || [];
          // Why the backend chose it, when it says - shown on the thumbnail's tooltip.
          line.imageNote = hit.imageUrl ? String(hit.imageNote || '') : '';
          // THE BOOSTER'S OWN PACK SHOT, sorted out of the box's pictures (03/10/2026). Written
          // to the booster's column only, and never over a picture somebody already gave it.
          if (hit.childImageUrl && line.child && !String(line.child.imageUrl || '').trim()) {
            await this.call('setImage', { rowId: line.rowId, imageUrl: hit.childImageUrl,
                                          code: line.code, target: 'child' });
            line.child.imageUrl = hit.childImageUrl;
          }
        } catch (error) { /* leave the plus button; the next visit tries again */ }
      }
    }
    // The caller redraws and reports. Said out loud there, because the whole fault was
    // that a refusal was indistinguishable from "these products have no artwork" - which
    // is what it looked like for a fortnight.
    return failed;
  }

  async loadPreOrders() {
    const url = this.dataset.preorderUrl;
    if (!url) return new Map();
    try {
      const answer = await (window.dcFetch || fetch)(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ secret: this.dataset.preorderSecret || this.secret,
                               operation: 'list' }),
      });
      if (!answer.ok) return new Map();
      const text = await answer.text();
      if (!text.trim()) return new Map();
      const data = JSON.parse(text);
      return new Map((data.products || []).map((p) => [Number(p.productId), p]));
    } catch (err) {
      return new Map();
    }
  }

  line(line) {
    const row = document.createElement('tr');
    row.className = 'deliveries__row';
    row.dataset.rowIds = (line.rowIds || [line.rowId]).join(',');
    // THE SUPPLIER CODE THIS ROW WAS DRAWN FROM, carried so a decision can state what it
    // expects to find. Stashed on the element rather than read from the line object
    // because `approveAllOnce` works from rows it collected off the page and has no line -
    // which is the same reason `rowIds` lives here. See `expectFor`.
    row.dataset.code = String(line.code || '');

    // NEW PRODUCT versus RESTOCK. They are different decisions - one puts a product on
    // the till that was never there, the other adds to something the shop already sells -
    // so a person must know which they are agreeing to before pressing anything.
    const isNew = line.kind === 'create';
    // Read once, up here, for the PO mark in the Type cell.
    const preOrder = (this.preOrders && this.preOrders.get(Number(line.tillId))) || null;
    // AN EMPTY MONEY CELL IS A DASH, NOT A BLANK (Jonathan, 15/08/2026). A blank reads as
    // a page that failed to load the figure; a dash says plainly that there isn't one.
    // The distinction matters most where it is true and normal - a product the till holds
    // no sale price for has genuinely never been priced, and that is worth seeing before
    // agreeing to book stock in against it.
    const money = (v) => (v === '' || v == null || v === 0 ? '\u2014' : '\u00a3' + Number(v).toFixed(2));

    // THE BADGE HAS ITS OWN COLUMN (Jonathan, 15/08/2026), rather than sitting above the
    // name. It is a property of the row like every other cell, and stacking it on the name
    // both made that cell two lines tall and put a classification where a reader scanning
    // down the names does not want one. A create row carries its badge here too - the
    // column always says which of the two decisions this line is.
    row.appendChild(this.imageCell(line, isNew));

    const kind = document.createElement('td');
    kind.className = 'deliveries__cell-kind';
    // THE PO MARK SITS CENTRED ABOVE THE TYPE PILL (Jonathan, 14/09/2026), not on the name:
    // being a pre-order is a property of the line like its type, so the two share a cell.
    // The badge itself is defined once in `stock-preorder-badge.css`, shared with Stock
    // processing and the Pre-Orders tab.
    const kindStack = document.createElement('div');
    kindStack.className = 'deliveries__kind-stack';
    if (preOrder) {
      const mark = document.createElement('span');
      mark.className = 'dc-po';
      mark.textContent = 'PO';
      mark.title = 'Pre-order';
      kindStack.appendChild(mark);
    }
    // A PRODUCT DELETED FROM THE TILL AND NOW BACK IS A RESTOCK TO THE PERSON APPROVING
    // (Jonathan, 21/10/2026), though the line still creates a till product; its triangle sits
    // beside the pill rather than the price.
    const returning = this.returningWarning(line);
    kindStack.appendChild(this.pillWithMark(this.badge(isNew && !returning), returning));
    kind.appendChild(kindStack);
    row.appendChild(kind);

    const name = document.createElement('td');
    name.className = 'deliveries__cell-name';
    // A RESTOCK'S NAMES ARE EDITABLE TOO (Jonathan, 15/09/2026). Its edits are written onto the
    // existing till product after approval, by the same route as a restock's retyped price.
    const website = this.websiteNameCell('description', line.description || '', !isNew);
    name.appendChild(this.editableText('name', line.name || '', 'deliveries__name-text',
      website.set, !isNew));
    // MATCHED TO ASMODEE'S PRODUCT BY NAME (Jonathan, 01/10/2026) - a guess, so it says so.
    const nameMatch = this.asmodeeNameWarning(line);
    if (nameMatch) name.appendChild(nameMatch);
    // Two new products under one name: the website refuses the second listing
    // (01/10/2026, eleven Reality Fracture designs). Approval refuses it too; this
    // shows it before anyone ticks.
    const clash = this.nameClashWarning(line);
    if (clash) name.appendChild(clash);
    row.appendChild(name);

    // A RESTOCK HAS A DESCRIPTION TOO, and it was being thrown away on the page (Jonathan,
    // 15/08/2026). The staging writes Asmodee's own wording for every row, restock
    // included - "One Piece Card Game: Booster Pack (OP-17)" against a till record named
    // "1P The Worlds Strongest Warriors (OP-17) Box" - and that wording is precisely what
    // somebody holding the paper note is checking the till's name against. Only the
    // EDITABLE version was ever rendered, so the column sat empty on every restock and
    // looked broken.
    const description = document.createElement('td');
    description.className = 'deliveries__cell-description';
    description.appendChild(website.cell);
    row.appendChild(description);

    // HOW MANY LINES THE PAPER NOTE SPLIT THIS ACROSS IS NOT SHOWN (Jonathan, 15/08/2026):
    // "we don't care how many lines it was on the original delivery note". The quantity is
    // the quantity. Amalgamating repeats of one product into one row still happens - it is
    // what makes 24 and 156 read as 180 - it is simply not annotated.
    const qty = document.createElement('td');
    qty.className = 'num';
    // ON ORDERED FROM SUPPLIERS THE QUANTITY IS WHAT WAS ORDERED (Jonathan, 15/09/2026: "We
    // don't seem to have picked up the quantities properly"). The row itself carries 0 on
    // purpose - Create books nothing on order, because the delivery note books the stock when
    // it comes - so the figure shown is the ordered count staging wrote beside it.
    // ONLY WHAT ASMODEE CONFIRMED (Jonathan, 04/10/2026): the confirmed figure when staging
    // found one on Asmodee's Open Order Summary, else the ordered figure.
    const ordered = this.preorderMode()
      ? String(line.detail || '').match(/^Pre-order: (\d+) ordered(?: \(\d+ cases of \d+ on the order\))?(?:, (\d+) confirmed by Asmodee)?/)
      : null;
    qty.textContent = ordered ? (ordered[2] != null ? ordered[2] : ordered[1]) : line.units;
    // NO WEBSITE FIGURE BESIDE A PRE-ORDER'S QUANTITY (Jonathan, 14/09/2026): "I think it's
    // confusing. I don't think we need it." The 30/08 "(website N)" note is removed; the PO
    // badge still marks the line.
    row.appendChild(qty);

    const cost = document.createElement('td');
    cost.className = 'num';
    cost.textContent = money(line.costPrice);
    // A RESTOCK WHOSE COST HAS RISEN SAYS SO (Jonathan, 10/09/2026). Appended rather than
    // replacing the figure: the number is what the eye is scanning for, and the triangle
    // is a note beside it.
    const warning = this.costWarning(line);
    if (warning) { cost.appendChild(document.createTextNode(' ')); cost.appendChild(warning); }
    row.appendChild(cost);

    // The shop's own price and NOTHING ELSE - deliberately not falling back to the RRP
    // when it is missing. A fallback here would put a supplier's suggested retail under a
    // heading that just says "Price", which is exactly the confusion this column was
    // changed to end, and the shorter heading makes it easier to slip past unnoticed.
    //
    // EDITABLE SINCE 10/09/2026, and a `?` where nothing could work it out. The backend no
    // longer refuses to stage a line it cannot price, so this cell is where that answer
    // gets supplied. It carries `data-edit`, so `editsFor()` sends whatever is on screen
    // exactly as it does for the name.
    //
    // EDITABLE ON A RESTOCK TOO (Jonathan, 10/09/2026): "The price needs to be editable for
    // restocks as well. When the warning triangle is displayed because the cost price has
    // increased, the user needs to be able to edit the selling price appropriately."
    //
    // THIS WAS CREATE-ONLY FOR HALF A DAY AND THE REASON IT CHANGED MATTERS. It was
    // create-only because a restock's price reached nothing - `Deliveries Ops` wrote only
    // `OnOrder` - and a field whose value goes nowhere is worse than no field, because it
    // reads as applied. The cost-rise triangle beside it is what makes the edit necessary:
    // there is no point telling somebody the supplier put their cost up if the one thing
    // they would want to do about it cannot be done here. `Deliveries Ops` now applies an
    // edited price on a restock, so the field has somewhere to land.
    const salePrice = document.createElement('td');
    salePrice.className = 'num';
    // A RESTOCK WHOSE RRP RISE WILL MOVE THE PRICE SHOWS THE NEW PRICE (21/10/2026) - the one
    // approval sets - so the figure and the rounded-down triangle beside it agree.
    salePrice.appendChild(this.editablePrice('salePrice', line.risePrice != null ? line.risePrice : line.salePrice));
    // ON THE PRICE'S OWN LINE (Jonathan, 21/10/2026), between the figure and the pencil: the
    // editable price is a block, so a triangle appended after it wrapped underneath.
    for (const mark of [this.priceRoundedDownWarning(line)]) {
      if (!mark) continue;
      const priceWrap = salePrice.querySelector('.deliveries__editable--price');
      const pencilButton = priceWrap && priceWrap.querySelector('.deliveries__edit');
      if (pencilButton) priceWrap.insertBefore(mark, pencilButton);
      else salePrice.appendChild(mark);
    }
    row.appendChild(salePrice);

    // THE SECTION, NOT THE ROUTE TO IT (Jonathan, 15/08/2026). "TCGs / One Piece / 1P
    // Boxes" is three shelves' worth of prose to say "1P Boxes", and at this width it
    // pushed the row wide for no gain: the person approving knows where 1P Boxes is. The
    // whole path stays on the cell's tooltip, because the leaf alone is ambiguous across
    // the till's 138 sections and the tree is occasionally the thing being checked.
    const category = document.createElement('td');
    if (isNew) {
      const select = this.categoryField(line);
      category.appendChild(select);
      this.appendBgg(category, line);
      this.appendTags(category, line.tags, select);
    } else if (line.categoryPath) {
      category.textContent = this.leafCategory(line.categoryPath);
      category.title = line.categoryPath;
    }
    if (this.preorderMode()) row.appendChild(this.releaseCell(line));
    row.appendChild(category);

    const actions = document.createElement('td');
    actions.className = 'deliveries__actions';
    const tick = document.createElement('button');
    tick.type = 'button';
    tick.className = 'deliveries__tick';
    tick.setAttribute('aria-label', 'Approve');
    tick.innerHTML = '&#10003;';
    tick.addEventListener('click', () => this.decide(row, line, 'approve'));
    const cross = document.createElement('button');
    cross.type = 'button';
    cross.className = 'deliveries__cross';
    cross.setAttribute('aria-label', 'Reject');
    cross.innerHTML = '&#10007;';
    cross.addEventListener('click', () => this.decide(row, line, 'reject'));
    // THE TICK AND CROSS KEEP THEIR OWN ROW, AND SPLIT SITS UNDER THEM (Jonathan,
    // 12/09/2026). Two decisions about this line, then a third control that changes what
    // the line IS rather than deciding it - so it reads as a different kind of thing
    // without needing a label to say so.
    //
    // THE STACK IS WHAT CENTRES THE SPLIT BUTTON (Jonathan, 12/09/2026). The cell is
    // right-aligned, so on its own the split button sat under the CROSS rather than under
    // the pair. The stack is an inline-block whose width comes from the pair - the widest
    // thing in it - so it still hugs the right edge of the cell, while centring its own
    // contents puts the split button under the middle of the two above it.
    const stack = document.createElement('div');
    stack.className = 'deliveries__actions-stack';
    const pair = document.createElement('div');
    pair.className = 'deliveries__actions-pair';
    pair.appendChild(tick);
    // ON THE ORDERED-FROM-SUPPLIERS LIST THE TICK SAYS "Create Preorder" AND THERE IS NO
    // CROSS (Jonathan, 15/09/2026; renamed 02/10/2026). Creating the product is the only
    // decision made there, and it is always to take pre-orders: `Pre-Order Watch` flags the
    // listing once it exists and puts it in the Pre-Orders! collection by itself.
    if (this.preorderMode()) {
      tick.textContent = 'Create Preorder';
      tick.classList.add('deliveries__tick--create');
      tick.setAttribute('aria-label', 'Create Preorder');
    } else {
      pair.appendChild(cross);
    }
    stack.appendChild(pair);
    stack.appendChild(this.splitButton(row, line));
    actions.appendChild(stack);
    row.appendChild(actions);
    return row;
  }

  /**
   * SPLIT: ONE SUPPLIER LINE THAT IS REALLY SEVERAL PRODUCTS (Jonathan, 12/09/2026).
   *
   * His example is `FAB Silver Age Decks Usurp the Shadow Throne Display (6)`, which
   * contains two different decks - Prism and Viserai - and arrives as one line. Splitting
   * it gives him two pending rows to rename, rather than one till product that is a
   * fiction.
   *
   * IT IS NOT A DECISION, so it is not beside the tick and cross: it changes the shape of
   * what is waiting, and the rows it makes still have to be approved afterwards.
   */
  splitButton(row, line) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'deliveries__split';
    button.setAttribute('aria-label', 'Split into several products');
    button.title = 'Split this line into several products';
    // AN AMALGAMATED LINE CANNOT BE SPLIT, and it is off rather than refusing after the
    // fact. Several review rows are merged into one line where a note repeats a product,
    // so "split this into three" has no single row to answer for - and the server refuses
    // it for that reason. Saying so before the dialogue opens is kinder than after.
    if ((line.rowIds || []).length > 1) {
      button.disabled = true;
      button.dataset.alwaysOff = 'yes';
      button.title = 'This line covers more than one row of the delivery note, so it '
        + 'cannot be split.';
    } else if (line.kind !== 'create') {
      // NEW PRODUCTS ONLY (Jonathan, 12/09/2026): *"we only want to allow this for new
      // products, not restocks."* A restock points at a till product that already exists,
      // so two rows would both add to it and book the delivery twice.
      button.disabled = true;
      button.dataset.alwaysOff = 'yes';
      button.title = 'Only a new product can be split. This line adds to a product the '
        + 'till already has.';
    } else if (this.isBoosterBox(line)) {
      // NOT A BOOSTER BOX (his instruction, same message). One box has one loose booster,
      // so splitting it would make a booster per piece for a booster that exists once.
      button.disabled = true;
      button.dataset.alwaysOff = 'yes';
      button.title = 'A booster box cannot be split.';
    } else if (!this.splitOptions(line.units).length) {
      // NOTHING DIVIDES INTO IT. A quantity of one, or of nothing, has no honest split -
      // and a prime quantity offers only itself, which is still a real answer. Off with
      // the reason showing beats a dialogue that opens on an empty list.
      button.disabled = true;
      button.dataset.alwaysOff = 'yes';
      button.title = `${line.units} cannot be divided between separate products, so this `
        + 'line cannot be split.';
    }
    // TWO LEGS AT THE TOP MEETING A STEM AT THE BOTTOM - a Y. It was the other way up until
    // Jonathan asked for it inverted on 12/09/2026. Drawn rather than a character for the
    // same reason the booster's arrow is: a glyph depends on whatever font the tablet
    // resolves.
    button.innerHTML = '<svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true" '
      + 'fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" '
      + 'stroke-linejoin="round"><path d="M12 20v-4"/><path d="M12 16L6 10V4"/>'
      + '<path d="M12 16l6 -6V4"/></svg>';
    button.addEventListener('click', () => this.split(row, line));
    return button;
  }

  /**
   * The loose booster that will be created beneath a booster box, or the reason there
   * will not be one. Returns null for every ordinary line, which is most of them.
   *
   * IT IS NOT POSSIBLE TO ORDER LOOSE BOOSTERS, ONLY BOXES - so a booster is never a line
   * on a delivery note and never has a quantity of its own. Its stock lives on the box:
   * the box's record counts sealed boxes and the loose boosters inside them. That is why
   * the Qty column here is a dash rather than a number, and why there is no tick.
   */
  /**
   * THE BOOSTER, SHAPED LIKE A LINE, so the picture cell, the upload and the undo all work on
   * it unchanged rather than growing a second copy each.
   *
   * It is the BOX'S review row (`rowId`) - a booster has no row of its own - with `target:
   * 'child'`, which is what sends every write to the booster's picture column instead of the
   * box's. The picture itself is read from and written to `line.child`, so a redraw after the
   * picture lookups keeps it.
   */
  boosterSlot(line) {
    const child = line.child;
    return {
      rowId: line.rowId,
      tillId: null,
      kind: 'create',
      target: 'child',
      name: child.name || '',
      get imageUrl() { return child.imageUrl || ''; },
      set imageUrl(value) { child.imageUrl = value; },
    };
  }

  boosterRow(line) {
    if (line.kind !== 'create') return null;
    const child = line.child;
    const refusal = String(line.childRefusal || '').trim();
    if (!child && !refusal) return null;

    const row = document.createElement('tr');
    row.className = 'deliveries__row deliveries__row--child';

    // THE BOOSTER'S OWN PICTURE, IN THE IMAGE COLUMN (Jonathan, 11/09/2026). The parent row
    // builds NINE cells - image, type, till name, website name, qty, cost, price, category,
    // actions - so this row needs a leading cell to line up, and it used to be an empty one.
    //
    // IT IS THE BOOSTER'S PICTURE AND NEVER THE BOX'S. Asmodee publish the box, so the
    // lookup usually finds nothing for a loose booster, and the box's pack shot is the one
    // fallback that looks right and is not: "I never want somebody to buy a booster thinking
    // that they are buying a box" (24/08/2026). So the cell starts as a plus, a person
    // supplies one, and it is stored in the row's own booster column for approval to carry
    // through - see `boosterSlot`.
    //
    // A REFUSED BOOSTER KEEPS THE EMPTY CELL: there is no product to give a picture to.
    row.appendChild(child
      ? this.imageCell(this.boosterSlot(line), true)
      : document.createElement('td'));

    const kind = document.createElement('td');
    kind.className = 'deliveries__cell-kind';
    // THE INDENT MARK SITS LEFT OF THE PILL, AND IT IS AN ICON (Jonathan, 12/09/2026).
    //
    // It used to be a `↳` glyph on the NAME cell's `::before`, two columns to the right of
    // the badge - so the one thing it exists to say, that this row belongs to the row above,
    // was announced after the reader had already passed the pill that names it. At the size a
    // text glyph renders it was also barely visible against the row's own pale background.
    //
    // Drawn rather than typed: a glyph is at the mercy of the font, and this one has to read
    // as a branch at a glance across the width of the table.
    //
    // THE ARROW AND THE PILL SIT IN AN INNER DIV, and that is load-bearing rather than
    // tidiness: laying them out by making the CELL a flex container stops the `<td>` being
    // a table-cell at all, so it no longer stretches to the row's height. The pair then sat
    // at the top of the row, and the cell's own `border-bottom` drew under the arrow
    // instead of across the row. See the note beside `.deliveries__child-kind`.
    const inner = document.createElement('div');
    inner.className = 'deliveries__child-kind';
    inner.appendChild(this.childArrow());
    const badge = document.createElement('span');
    badge.className = 'deliveries__badge deliveries__badge--'
      + (child ? 'booster' : 'missing');
    badge.textContent = child ? 'Booster' : 'No booster';
    inner.appendChild(this.pillWithMark(badge, child ? this.returningWarning(child) : null));
    kind.appendChild(inner);
    row.appendChild(kind);

    if (!child) {
      // WHY THERE IS NO BOOSTER, ACROSS THE REST OF THE ROW. A booster box arriving on the
      // till without its loose booster is a real half-made set, and the person approving is
      // the only one who will ever be in a position to notice. Saying nothing would make it
      // look as though this set simply does not have one.
      const why = document.createElement('td');
      why.colSpan = this.preorderMode() ? 7 : 6;
      why.className = 'deliveries__child-note';
      why.textContent = refusal;
      row.appendChild(why);
      row.appendChild(document.createElement('td'));
      return row;
    }

    const money = (v) => (v === '' || v == null || v === 0 ? '—' : '£' + Number(v).toFixed(2));

    // BOTH NAMES ARE EDITABLE, EXACTLY AS THE BOX'S ARE (Jonathan, 12/09/2026). The booster
    // is named by deriving from the previous set's booster, so it is right most of the time
    // and occasionally not - and until today it was the only created product on this page
    // whose name could not be corrected before it was made. Renaming the BOX made that
    // worse rather than better: the two then disagreed with no way to settle it.
    //
    // `childName` / `childDescription`, NOT `name` / `description`. `editsFor()` merges this
    // row's `[data-edit]` fields into the BOX's edits keyed by name, so a shared key would
    // silently overwrite the box's own name with the booster's. The `child` prefix is the
    // convention `childCategoryId` and `childSalePrice` already set for that reason.
    const name = document.createElement('td');
    name.className = 'deliveries__cell-name';
    const website = this.websiteNameCell('childDescription', child.description || '');
    name.appendChild(
      this.editableText('childName', child.name || '', 'deliveries__name-text', website.set));
    row.appendChild(name);

    // THE WEBSITE NAME, WHICH IS WHAT THE TILL'S DESCRIPTION BECOMES. It used to hold the
    // fixed sentence "created with the box, and linked to it" - true, and it said the same
    // thing on every booster there has ever been, in the one column where the row's own
    // value belongs.
    //
    // The booster's reasoning - which previous set it followed, where its cost and price
    // came from - stays as the cell's tooltip, which is where it was already: it is long,
    // and it is only wanted when something looks wrong.
    const description = document.createElement('td');
    description.className = 'deliveries__cell-description';
    description.appendChild(website.cell);
    if (child.detail) description.title = child.detail;
    row.appendChild(description);

    const qty = document.createElement('td');
    qty.className = 'num';
    // NOT A ZERO. Zero would say the shop is getting none, when what is true is that a
    // booster has no quantity of its own at all - the boxes above are the quantity.
    qty.textContent = '—';
    qty.title = 'Loose boosters cannot be ordered. Its stock lives on the box above.';
    row.appendChild(qty);

    const cost = document.createElement('td');
    cost.className = 'num';
    cost.textContent = money(child.costPrice);
    row.appendChild(cost);

    // `childSalePrice`, NOT `salePrice`. `editsFor()` merges the booster's `[data-edit]`
    // fields into the BOX's edits keyed by name, so a shared key would silently overwrite
    // the box's own price with the booster's - an £85 box saved at £5. The `child` prefix
    // is the convention `childCategoryId` already set for exactly this reason.
    const salePrice = document.createElement('td');
    salePrice.className = 'num';
    salePrice.appendChild(this.editablePrice('childSalePrice', child.salePrice));
    row.appendChild(salePrice);
    // The booster is released with its box; the box row carries the date.
    if (this.preorderMode()) row.appendChild(this.releaseCell(null));

    // A DROPDOWN, NOT TEXT (Jonathan, 22/08/2026). The booster's section is inherited from
    // the previous set's own booster, which is the best evidence there is and is still
    // sometimes the shop being loose: LM-07's booster sits in the bare `TCGs / Digimon`
    // rather than a Boosters section, so LM-08's would too. This is the one field on the
    // row most likely to want correcting, and it was the one field that could not be.
    const category = document.createElement('td');
    const childSelect = this.categoryField(
      { categoryId: child.categoryId }, 'childCategoryId');
    category.appendChild(childSelect);
    if (child.categoryPath) category.title = child.categoryPath;
    this.appendTags(category, child.tags, childSelect);
    row.appendChild(category);

    // No tick and no cross: the box above carries the decision for both.
    row.appendChild(document.createElement('td'));
    return row;
  }

  /**
   * THE PREVIOUS SET'S COST AND PRICE, UNDER A NEW LINE THAT COSTS MORE (Jonathan, 22/08/2026:
   * "If the cost price of the new set is more than the previous set ... we should display
   * (indented) the cost price and sale price of the previous set underneath. That makes it much
   * easier for the user to work out a new sale price."). Staged by the planner only when this
   * line costs MORE than the product it would have followed. Read-only: it is a reference for
   * the person typing the price into the row above, not something that is created.
   */
  priorRow(line) {
    const prior = line.priorFigures;
    if (!prior || line.kind !== 'create') return null;
    const money = (v) => (v === '' || v == null || !(Number(v) > 0) ? '—' : '£' + Number(v).toFixed(2));
    const row = document.createElement('tr');
    row.className = 'deliveries__prior';
    row.appendChild(document.createElement('td'));
    const kind = document.createElement('td');
    kind.className = 'deliveries__cell-kind';
    const inner = document.createElement('div');
    inner.className = 'deliveries__child-kind';
    inner.appendChild(this.childArrow());
    const badge = document.createElement('span');
    badge.className = 'deliveries__badge deliveries__badge--prior';
    // WHICH PRODUCT THIS IS, SAID PLAINLY. Found by the set code it is the previous set; found
    // only as the newest product on the same shelf it may be something quite different - a
    // Mastery Pack beside an Armory Deck - and calling that "the last set" would mislead.
    const bySet = prior.route === 'set code';
    const bySeries = prior.route === 'series';
    badge.textContent = bySet ? 'Last set' : (bySeries ? 'Last in series' : 'Previous');
    inner.appendChild(badge);
    kind.appendChild(inner);
    row.appendChild(kind);
    const name = document.createElement('td');
    name.className = 'deliveries__cell-name';
    name.textContent = prior.name || 'the previous product';
    row.appendChild(name);
    const note = document.createElement('td');
    note.className = 'deliveries__child-note';
    // ITS RRP TOO (13/10/2026), so the person can compare it with this line's own.
    const priced = Number(line.salePrice) > 0;
    note.textContent = (bySet ? 'The previous set. '
      : (bySeries ? 'The newest in the ' + (prior.series || 'same') + ' series. '
        : 'The newest product on the same shelf. '))
      + (Number(prior.rrPrice) > 0 ? 'RRP £' + Number(prior.rrPrice).toFixed(2) + '. ' : 'No RRP. ')
      + (priced ? 'This one costs more - check its price against these.'
        : 'This one costs more, so its price was not carried over.');
    row.appendChild(note);
    row.appendChild(document.createElement('td'));
    const cost = document.createElement('td');
    cost.className = 'num';
    cost.textContent = money(prior.costPrice);
    row.appendChild(cost);
    const price = document.createElement('td');
    price.className = 'num';
    price.textContent = money(prior.salePrice);
    row.appendChild(price);
    if (this.preorderMode()) row.appendChild(document.createElement('td'));
    row.appendChild(document.createElement('td'));
    row.appendChild(document.createElement('td'));
    return row;
  }

  /** A pill with its warning triangle on the same line, or the pill alone. */
  pillWithMark(pill, mark) {
    if (!mark) return pill;
    const pair = document.createElement('span');
    pair.className = 'deliveries__pill-mark';
    pair.append(pill, mark);
    return pair;
  }

  badge(isNew) {
    const badge = document.createElement('span');
    badge.className = 'deliveries__badge deliveries__badge--' + (isNew ? 'new' : 'restock');
    // One word (Jonathan, 02/10/2026): the column is already about what kind of line it is.
    badge.textContent = isNew ? 'New' : 'Restock';
    return badge;
  }

  /**
   * THE MARK THAT SAYS THIS ROW BELONGS TO THE ONE ABOVE.
   *
   * Drawn, not typed. The `↳` character it replaces was at the mercy of whatever font the
   * tablet resolved, and at body size it read as a speck - which is no use for the one
   * thing it has to say from across a wide table.
   *
   * `aria-hidden`, because the badge beside it already says "Booster" in words. A screen
   * reader gaining "corner down right arrow" before every one of those is noise.
   */
  childArrow() {
    // NO TOOLTIP. The sentence it would carry is the one Jonathan had removed from the row
    // on 12/09/2026 - the arrow and the pill beside it already say this belongs to the box
    // above, and re-stating it on hover is the same text in a quieter place.
    const wrap = document.createElement('span');
    wrap.className = 'deliveries__child-arrow';
    wrap.innerHTML = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" '
      + 'fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" '
      + 'stroke-linejoin="round"><path d="M5 4v8a4 4 0 0 0 4 4h9"/><path d="M14 11l5 5-5 5"/>'
      + '</svg>';
    return wrap;
  }

  input(key, value) {
    const input = document.createElement('input');
    input.type = 'text';
    input.value = value;
    input.dataset.edit = key;
    return input;
  }

  /**
   * An editable field that READS as text until somebody wants to change it.
   *
   * JONATHAN, 22/08/2026: "It's too difficult to read the names and descriptions when
   * they're text boxes. Most of the text doesn't display."
   *
   * He is right, and the reason is that a single-line `<input>` cannot wrap: at this
   * column width `Digimon Card Game: Booster Pack - Timeless Bonds (BT-26)` showed as
   * `Digimon Card Game: Boc`, and the part that identifies WHICH product it is - the set
   * name - is the part that fell off the end. The page exists so somebody can check a row
   * against the paper note, and it was hiding the very words being checked.
   *
   * So the text is a plain wrapping span with a pencil beside it, and the input appears
   * only while it is being edited.
   *
   * THE HIDDEN FIELD KEEPS ITS `data-edit`, WHICH IS WHY NOTHING DOWNSTREAM CHANGES.
   * `decide()` collects edits with `row.querySelectorAll('[data-edit]')` and reads
   * `.value`; a hidden textarea answers both exactly as the old input did, so approve and
   * reject are untouched and a value survives whether or not it was ever edited.
   *
   * A TEXTAREA RATHER THAN AN INPUT, because the thing being fixed is that these strings
   * are longer than one line - editing them in a box that cannot show them either would
   * only move the problem.
   */
  /**
   * A PRICE THAT CAN BE TYPED IN, and a `?` when nobody knows it yet.
   *
   * JONATHAN, 10/09/2026: "If the supplier provides an RRP, we use that. Otherwise, if
   * it's a booster product, we check the previous set, and if we can't figure it out, we
   * stick a question mark in there and allow the user to edit it manually."
   *
   * So an unpriced row is no longer a refusal to stage - it stages with a `?` and somebody
   * types the answer. That only works if the `?` cannot be approved by accident, which is
   * what `unpricedIn()` below is for.
   *
   * A ONE-LINE `<input>`, NOT THE TEXTAREA `editableText` USES. The 22/08 reason for a
   * textarea was that names are longer than the box showing them; a price is four
   * characters and a three-row box for it would be absurd.
   *
   * THE FIELD HOLDS THE NUMBER AND THE SPAN HOLDS THE POUND SIGN. `editsFor()` reads
   * `.value` straight off `[data-edit]` and sends it to the backend, so a value of
   * `£4.50` would arrive as a string no arithmetic can use. The span is the only place
   * money is ever formatted.
   */
  editablePrice(key, value) {
    const known = !(value === '' || value == null || Number(value) === 0);

    const wrap = document.createElement('div');
    wrap.className = 'deliveries__editable deliveries__editable--price';

    const text = document.createElement('span');
    text.className = 'deliveries__editable-text deliveries__price-text';
    const paint = () => {
      const n = Number(field.value);
      const ok = field.value !== '' && Number.isFinite(n) && n > 0;
      text.textContent = ok ? '£' + n.toFixed(2) : '?';
      // The `?` is the thing somebody is meant to notice and act on, so it says so rather
      // than reading as a missing figure nobody has to do anything about.
      text.classList.toggle('deliveries__price-text--unknown', !ok);
      text.title = ok ? '' : 'No price could be worked out. Type one in.';
    };

    const pencil = document.createElement('button');
    pencil.type = 'button';
    pencil.className = 'deliveries__edit';
    pencil.setAttribute('aria-label', 'Edit price');
    pencil.title = 'Edit price';
    pencil.innerHTML = '<svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" '
      + 'fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" '
      + 'stroke-linejoin="round"><path d="M11.5 2.5l2 2L6 12l-3 1 1-3z"/></svg>';

    const field = document.createElement('input');
    field.type = 'text';
    // `inputmode` rather than `type="number"`: a number input silently discards a value it
    // cannot parse, so a mistyped price would vanish instead of being visibly wrong.
    field.inputMode = 'decimal';
    field.dataset.edit = key;
    field.value = known ? Number(value).toFixed(2) : '';
    field.className = 'deliveries__editable-input deliveries__price-input';
    field.hidden = true;

    const show = () => {
      wrap.classList.add('is-editing');
      text.hidden = true;
      pencil.hidden = true;
      field.hidden = false;
      field.focus();
      field.select();
    };
    const hide = () => {
      const n = Number(field.value);
      // Normalise on the way out so what is sent and what is shown cannot disagree: `4.5`
      // and `4.50` are the same price, and `£4.50` or `abc` is not a price at all.
      if (field.value !== '' && Number.isFinite(n) && n > 0) field.value = n.toFixed(2);
      else field.value = '';
      wrap.classList.remove('is-editing');
      paint();
      text.hidden = false;
      pencil.hidden = false;
      field.hidden = true;
    };

    pencil.addEventListener('click', show);
    text.addEventListener('click', show);
    field.addEventListener('blur', hide);
    field.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') { event.preventDefault(); hide(); }
      if (event.key === 'Escape') { field.value = known ? Number(value).toFixed(2) : ''; hide(); }
    });

    paint();
    wrap.appendChild(text);
    wrap.appendChild(pencil);
    wrap.appendChild(field);
    return wrap;
  }

  /**
   * THE COST WENT UP SINCE LAST TIME, on a restock.
   *
   * JONATHAN, 10/09/2026: "Can you add a warning triangle to any cost price increase for a
   * restock... If the Cost price has increased, we should also check to see if the RRP has
   * increased as well and display that in the same tooltip."
   *
   * `costWas` is present ONLY when the backend has established a rise against the till's
   * own figure, so the triangle is drawn from its presence rather than from any comparison
   * done here - the page has no idea what the till holds.
   *
   * THE RRP LINE IS OMITTED RATHER THAN GUESSED. 35% of the till's products carry no RRP
   * at all, so a comparison against a blank one would announce a rise on a third of every
   * restock. The backend sends `rrpWas`/`rrpNow` only when both are genuinely known.
   */
  priorWarning(prior) {
    const which = prior.route === 'set code' ? 'the previous set'
      : 'the last in the ' + (prior.series || 'same') + ' series';
    const mark = document.createElement('span');
    mark.className = 'deliveries__cost-warning';
    mark.setAttribute('role', 'img');
    mark.setAttribute('aria-label', 'Costs more than ' + which + '. Check the price.');
    mark.textContent = '⚠';
    // The figures it is compared with sit on the row underneath, so the tooltip only says what
    // to do (Jonathan, 21/10/2026).
    mark.title = 'Check the price.';
    return mark;
  }

  nameClashWarning(line) {
    const said = String(line.nameClash || '').trim();
    if (!said) return null;
    const mark = document.createElement('span');
    mark.className = 'deliveries__cost-warning deliveries__name-clash';
    mark.setAttribute('role', 'img');
    mark.setAttribute('aria-label', 'Another product would get the same name');
    mark.textContent = ' ⚠';
    mark.title = said.replace(/NAME CLASH: /g, '');
    return mark;
  }

  /**
   * THIS LINE WAS JOINED TO ASMODEE'S PRODUCT BY NAME.
   *
   * JONATHAN, 01/10/2026: "Whenever we do this, if we find a match, we should display a
   * warning triangle on the delivery line. Hovering over that warning triangle should display
   * a message that says which product we matched it to on the Asmodee list, and then the user
   * can double-check that it is the right product before approving it."
   *
   * Bliss publish no barcode, so a Bliss line can only be recognised as the product the shop
   * already buys from Asmodee by its name. Staging writes `ASMODEE NAME MATCH: ...` into the
   * line's detail whenever it does that; the triangle is drawn from that sentence and nothing
   * else, so the page cannot announce a match the backend did not make.
   */
  /**
   * THE PROPOSED PRICE WAS ROUNDED DOWN ONTO THE SHOP'S STEP (Jonathan, 21/10/2026): "I'd rather
   * the price was filled in and a warning triangle is displayed, so the user can see how much it
   * was rounded." Staging writes `PRICE ROUNDED DOWN: £26.55 rounded down to £26.50, £0.05 less`
   * into the line's detail; the triangle is drawn from that sentence and nothing else.
   */
  priceRoundedDownWarning(line) {
    // A CREATED LINE ONLY. A restock's rounding is told on its cost triangle instead (21/10/2026).
    const found = String(line.detail || '').match(/PRICE ROUNDED DOWN: (£[\d.]+) rounded down to (£[\d.]+), (£[\d.]+) less/);
    if (!found) return null;
    const mark = document.createElement('span');
    mark.className = 'deliveries__cost-warning';
    mark.setAttribute('role', 'img');
    mark.setAttribute('aria-label', 'Price rounded down');
    mark.textContent = '⚠';
    mark.title = 'Rounded down from ' + found[1] + ' to ' + found[2] + '.';
    return mark;
  }

  /**
   * THIS PRODUCT WAS ON THE TILL BEFORE AND WAS DELETED (Jonathan, 21/10/2026): its price, section
   * and barcode were carried over from that record. Staging's detail says so in a sentence
   * starting `RETURNING PRODUCT:`; the tooltip is his wording.
   */
  returningWarning(line) {
    if (!/^RETURNING PRODUCT:/.test(String(line.detail || ''))) return null;
    const mark = document.createElement('span');
    mark.className = 'deliveries__cost-warning';
    mark.setAttribute('role', 'img');
    mark.setAttribute('aria-label', 'Original product deleted from the till');
    mark.textContent = '⚠';
    mark.title = 'Original product deleted from the till';
    return mark;
  }

  asmodeeNameWarning(line) {
    const found = String(line.detail || '').match(/ASMODEE NAME MATCH: matched by name to Asmodee's "([^"]*)" \(([^)]*)\)/);
    if (!found) return null;
    const mark = document.createElement('span');
    mark.className = 'deliveries__cost-warning deliveries__name-match';
    mark.setAttribute('role', 'img');
    mark.setAttribute('aria-label', 'Matched to an Asmodee product by name');
    mark.textContent = ' ⚠';
    mark.title = 'Matched by name to Asmodee\'s "' + found[1] + '" (' + found[2] + ').'
      + ' Bliss print no barcode, so the name is the only link - check this is the same'
      + ' product before approving.';
    return mark;
  }

  costWarning(line) {
    // A NEW PRODUCT THAT COSTS MORE THAN THE PREVIOUS SET OR THE LAST IN ITS SERIES (Jonathan,
    // 13/10/2026): "we should still prefer RRPs ... We just display the warning triangle if the
    // cost has increased, so that the user can compare". The figures are in the row beneath.
    if (line.kind === 'create' && line.priorFigures) return this.priorWarning(line.priorFigures);
    // ONLY A COST RISE THE RRP DOES NOT ANSWER (Jonathan, 18/10/2026): "We just want the warning
    // triangle if the cost price increases but the RRP doesn't, or if the cost price increases and
    // the product doesn't have an RRP." An RRP rise on a product sold at RRP moves the till's price
    // by itself at approval, so it needs no warning. `costWas` is sent only for a genuine cost rise;
    // `rrpWas`/`rrpNow` whenever both RRPs are known.
    // AND AN RRP RISE ON A PRODUCT SOLD BELOW ITS RRP (his answer, same day): that price is a
    // deliberate one, so it is not moved automatically and a person is shown the rise. The backend
    // says which with `atRrp` (false = the till's price is below its RRP) and sends `tillPrice`.
    const known = (v) => v !== '' && v != null && Number.isFinite(Number(v)) && Number(v) > 0;
    const money = (v) => '£' + Number(v).toFixed(2);
    const costRose = known(line.costWas);
    const hasRrp = known(line.rrpWas) && known(line.rrpNow);
    const rrpRose = hasRrp && Number(line.rrpNow) > Number(line.rrpWas);
    const belowRrp = rrpRose && line.atRrp === false;
    // AN RRP RISE AT RRP THAT APPROVAL CANNOT APPLY, because the new figure would only round DOWN
    // onto the price step (the shop's pricing rule): the backend says why in `rrpRiseHeld`.
    const held = rrpRose && !belowRrp ? String(line.rrpRiseHeld || '').trim() : '';
    // AN RRP RISE THAT APPROVAL APPLIES WITH A DOWNWARD ROUND (Jonathan, 21/10/2026): the triangle
    // is HERE, on the cost, saying what changed - not on the price column.
    const rounded = rrpRose && !belowRrp && !held
      ? String(line.priceRoundedDown || '').match(/PRICE ROUNDED DOWN: (£[\d.]+) rounded down to (£[\d.]+)/) : null;
    if (!(costRose && !rrpRose) && !belowRrp && !held && !rounded) return null;

    const mark = document.createElement('span');
    mark.className = 'deliveries__cost-warning';
    mark.setAttribute('role', 'img');
    mark.setAttribute('aria-label', belowRrp ? 'RRP increased' : 'Cost price increased');
    mark.textContent = '⚠';

    const lines = [];
    if (costRose) lines.push('Cost price increased from ' + money(line.costWas) + '.');
    if (belowRrp) {
      // JUST THE TWO FACTS (Jonathan, 21/10/2026): the cost and the RRP, nothing after them.
      lines.push('RRP increased from ' + money(line.rrpWas) + ' to ' + money(line.rrpNow) + '.');
    } else if (held) {
      lines.push('RRP increased from ' + money(line.rrpWas) + ' to ' + money(line.rrpNow) + '.');
      lines.push(held);
    } else if (rounded) {
      lines.push('RRP increased from ' + money(line.rrpWas) + ' to ' + money(line.rrpNow) + '.');
      lines.push('Price rounded down from ' + rounded[1] + ' to ' + rounded[2] + '.');
    } else {
      lines.push(hasRrp ? 'The RRP has not changed (' + money(line.rrpNow) + ').' : 'This product has no RRP.');
    }
    mark.title = lines.join(' ');
    return mark;
  }

  /**
   * THE WEBSITE NAME IS NOT EDITED, IT IS WORKED OUT (Jonathan, 15/09/2026): *"the website name
   * is always identical to the till name, except for shortcodes being expanded"*. So it is shown
   * as plain text and set from the till name whenever that is edited, by the same rule staging
   * uses (`website-name.js`, generated from it). The hidden field keeps it in `editsFor()`, so
   * what is on screen is still what is created.
   */
  websiteNameCell(key, value, onlyIfEdited) {
    const wrap = document.createElement('div');
    wrap.className = 'deliveries__website-name';
    const text = document.createElement('span');
    text.className = 'deliveries__description-text';
    text.textContent = value;
    const field = document.createElement('input');
    field.type = 'hidden';
    // ON A RESTOCK NOTHING IS SENT UNTIL SOMEBODY EDITS, because sending the till's own name back
    // on every approval would be a write request for a name nobody changed.
    if (!onlyIfEdited) field.dataset.edit = key;
    field.value = value;
    wrap.appendChild(text);
    wrap.appendChild(field);
    const set = (tillName) => {
      const rule = window.DiceCupWebsiteName;
      // NO RULE LOADED MEANS NO CHANGE, loudly. Guessing would put a till code in front of
      // customers, and silently keeping the old name would hide that the two now disagree.
      if (!rule) { this.say('The website name could not be updated - reload the page.', true); return; }
      const next = rule.websiteNameFor(tillName);
      text.textContent = next;
      field.value = next;
      field.dataset.edit = key;
    };
    return { cell: wrap, set };
  }

  editableText(key, value, textClass, onCommit, onlyIfEdited) {
    const wrap = document.createElement('div');
    wrap.className = 'deliveries__editable';

    const text = document.createElement('span');
    text.className = textClass + ' deliveries__editable-text';
    text.textContent = value;

    const pencil = document.createElement('button');
    pencil.type = 'button';
    pencil.className = 'deliveries__edit';
    pencil.setAttribute('aria-label', 'Edit ' + key);
    pencil.title = 'Edit ' + key;
    pencil.innerHTML = '<svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" '
      + 'fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" '
      + 'stroke-linejoin="round"><path d="M11.5 2.5l2 2L6 12l-3 1 1-3z"/></svg>';

    const field = document.createElement('textarea');
    if (!onlyIfEdited) field.dataset.edit = key;
    field.value = value;
    field.rows = 3;
    field.className = 'deliveries__editable-input';
    field.hidden = true;

    // THE FIELD REPLACES THE TEXT, IT DOES NOT SIT BESIDE IT (Jonathan, 22/08/2026).
    //
    // `hidden` alone did not do it. The text carries `.deliveries__name-text`, which sets
    // `display: block` - an AUTHOR rule, so it beats the browser's own `[hidden] { display:
    // none }` and the span stayed on screen. The field was then squeezed into whatever
    // space was left beside it, which is smaller than the box it replaced. The CSS now
    // hides these three by class+attribute, which outranks the single-class display rule;
    // the marker class here is what lets the wrapper stop being a row while editing.
    const show = () => {
      wrap.classList.add('is-editing');
      text.hidden = true;
      pencil.hidden = true;
      field.hidden = false;
      field.focus();
      field.setSelectionRange(field.value.length, field.value.length);
    };
    // The span follows the field, so what is on screen is always what would be sent.
    const hide = () => {
      const changed = text.textContent !== field.value;
      wrap.classList.remove('is-editing');
      text.textContent = field.value;
      text.hidden = false;
      pencil.hidden = false;
      field.hidden = true;
      if (changed) field.dataset.edit = key;
      if (changed && onCommit) onCommit(field.value);
    };

    pencil.addEventListener('click', show);
    // Clicking the text itself edits too - the pencil is the signpost, not the only way in.
    text.addEventListener('click', show);
    field.addEventListener('blur', hide);
    field.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); hide(); }
      // Escape puts back what was there, which a blur-to-commit field otherwise has no way
      // of offering.
      if (event.key === 'Escape') { field.value = text.textContent; hide(); }
    });

    wrap.appendChild(text);
    wrap.appendChild(pencil);
    wrap.appendChild(field);
    return wrap;
  }

  /**
   * THE TAGS THIS PRODUCT WILL BE CREATED WITH, under its section.
   *
   * Nothing read till tags until now, so nobody could see one being wrong: the sync does
   * not watch them and `dice_cup_product` does not store them, which is how the Toys
   * section spent months tagged where its products used to live. Showing them at the one
   * moment a person is already checking the section is the cheapest place to catch it.
   *
   * THE TAGS ARE COMPUTED AT APPROVAL, FROM THE FINAL SECTION - so what arrives here is a
   * PROPOSAL made against the section as staged. The instant somebody corrects that
   * dropdown the proposal is stale, and a stale tag list beside a corrected section is
   * precisely the fault this whole change exists to end. So a change to the dropdown
   * REPLACES the chips with a plain statement that they will be worked out again, rather
   * than leaving something that is quietly wrong on screen.
   *
   * The alternative - recomputing in the browser - would put the tag rules in a second
   * place, and two copies of one rule is how `money()` and `escapeHtml` bit us. The rules
   * stay in one workflow.
   *
   * Renders nothing at all when the backend sends no tags, so this is safe to ship before
   * the workflow half lands.
   */
  // THE BOARDGAMEGEEK MATCH THE PIPELINE FOUND (Jonathan, 27/10/2026), under the category,
  // so the person approving can see which game the line was taken to be. Only an exact
  // match is ever stored, so a blank means BGG was not asked or knew no such game.
  appendBgg(cell, line) {
    const name = String(line.bggName || '').trim();
    if (!name) return;
    const note = document.createElement('div');
    note.className = 'deliveries__bgg';
    const id = String(line.bggId || '').trim();
    if (/^\d+$/.test(id)) {
      const link = document.createElement('a');
      link.href = `https://boardgamegeek.com/boardgame/${id}`;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = `BGG: ${name}`;
      note.appendChild(link);
    } else {
      note.textContent = `BGG: ${name}`;
    }
    cell.appendChild(note);
  }

  appendTags(cell, tags, select) {
    const list = (Array.isArray(tags) ? tags : String(tags == null ? '' : tags).split('|'))
      .map((t) => String(t == null ? '' : t).trim())
      .filter(Boolean);
    if (!list.length) return;

    const wrap = document.createElement('div');
    wrap.className = 'deliveries__tags';
    list.forEach((tag) => {
      const chip = document.createElement('span');
      chip.className = 'deliveries__tag';
      chip.textContent = tag;
      wrap.appendChild(chip);
    });
    cell.appendChild(wrap);

    if (!select) return;
    const staged = select.value;
    select.addEventListener('change', () => {
      // Back to the staged section is back to the staged answer, so the chips are true
      // again - a person who opens the dropdown and changes their mind has changed nothing.
      if (select.value === staged) {
        wrap.className = 'deliveries__tags';
        wrap.textContent = '';
        list.forEach((tag) => {
          const chip = document.createElement('span');
          chip.className = 'deliveries__tag';
          chip.textContent = tag;
          wrap.appendChild(chip);
        });
        return;
      }
      wrap.className = 'deliveries__tags deliveries__tags--restated';
      wrap.textContent = 'Tags will be worked out from the section you picked.';
    });
  }

  /**
   * The last section of a till category path - "TCGs / One Piece / 1P Boxes" is "1P
   * Boxes". Split on the separator the backend joins with (' / ') rather than on a bare
   * slash, so a section whose own name carries one is not cut in half.
   */
  leafCategory(path) {
    const parts = String(path).split(' / ');
    return parts[parts.length - 1].trim() || String(path);
  }

  /**
   * The section dropdown, showing the LEAF - the same thing a restock row shows.
   *
   * THE CLOSED BOX SHOWS THE LEAF; THE OPEN LIST STILL SHOWS THE WHOLE TREE (Jonathan,
   * 07/09/2026 - "just display the lowest subcategory in the tree rather than the whole
   * breadcrumb here, like you do for restocks", then "the drop-down list should still
   * display the whole tree on each item").
   *
   * BOTH HALVES ARE NEEDED AND THEY CONTRADICT EACH OTHER. At this width "Accessories /
   * Card Folders & Pages" is cut off after the word every accessory shares, so the closed
   * control showed the least useful part of the path - but the till has 138 sections whose
   * leaves repeat across games, so a list of bare leaves would offer several
   * identical-looking wrong answers. Reading one back and picking one are different jobs.
   *
   * SO THE OPTION TEXT IS SWAPPED AS THE LIST OPENS, because a native select renders the
   * selected option's OWN text and there is no separate string for the closed state. Every
   * option gets its full path back on `mousedown`, `keydown` and `focus` - all of which
   * fire before the popup is drawn - and the selected one collapses to its leaf again on
   * `change` and `blur`. Two attempts at doing this with alignment failed first: `direction:
   * rtl` showed the end of the path rather than the leaf, and pairing it with `text-align`
   * silently undid it altogether.
   *
   * THE WHOLE PATH IS ALSO ON THE TOOLTIP, so hovering the closed box answers "which Games
   * Workshop?" without opening anything.
   *
   * `key` says which field it edits, because a booster box has TWO sections on screen -
   * its own and its loose booster's - and they are rarely the same one. Measured across
   * the till, a booster shares its box's section in 5 of 284 pairs.
   */
  categoryField(line, key) {
    const select = document.createElement('select');
    select.className = 'deliveries__category-select';
    select.dataset.edit = key || 'categoryId';

    // A LINE WITH NO CATEGORY MUST NOT LOOK LIKE ONE FILED IN THE FIRST SECTION
    // ALPHABETICALLY. The automation refuses rather than guessing when it cannot work a
    // category out, and the dropdown has to show that refusal rather than hide it behind
    // whatever happens to be at the top of the list.
    const none = document.createElement('option');
    none.value = '';
    none.textContent = line.categoryId ? '— none —' : '— pick a section —';
    select.appendChild(none);

    const pathOf = new Map();
    this.categories.forEach((cat) => {
      const option = document.createElement('option');
      option.value = String(cat.id);
      option.textContent = cat.path;
      option.title = cat.path;
      pathOf.set(option, cat.path);
      if (Number(cat.id) === Number(line.categoryId)) option.selected = true;
      select.appendChild(option);
    });

    // EVERY option is expanded, not just the selected one - the popup is drawn from all of
    // them. Collapsing only ever touches the selected one, because it is the only text the
    // closed control shows.
    const expand = () => pathOf.forEach((path, option) => { option.textContent = path; });
    const collapse = () => {
      const option = select.selectedOptions && select.selectedOptions[0];
      const path = pathOf.get(option);
      if (path) option.textContent = this.leafCategory(path);
      select.title = path || '';
    };
    select.addEventListener('mousedown', expand);
    select.addEventListener('keydown', expand);
    select.addEventListener('focus', expand);
    select.addEventListener('change', collapse);
    select.addEventListener('blur', collapse);
    collapse();
    // AS WIDE AS THE LONGEST TEXT IT CAN SHOW CLOSED (Jonathan, 02/10/2026) - a leaf name or
    // the "pick a section" prompt - never the full paths, which only appear in the open list
    // and get the room they need from the browser there.
    select.style.width = `calc(${this.categoryLeafWidth()}ch + 3.6rem)`;
    return select;
  }

  /** The longest text a closed category dropdown can show, in characters. */
  categoryLeafWidth() {
    if (!this.leafWidthCache) {
      const texts = ['— pick a section —', ...this.categories.map((c) => this.leafCategory(c.path))];
      this.leafWidthCache = Math.max(...texts.map((t) => String(t || '').length));
    }
    return this.leafWidthCache;
  }

  /** The booster row belonging to a box row, if it has one. */
  boosterRowOf(row) {
    const next = row.nextElementSibling;
    return next && next.classList.contains('deliveries__row--child') ? next : null;
  }

  /** Whatever is on screen for one row, which is what will actually be created. */
  editsFor(row) {
    const edits = {};
    row.querySelectorAll('[data-edit]').forEach((input) => {
      edits[input.dataset.edit] = input.value;
    });
    // THE BOOSTER'S FIELDS BELONG TO ITS BOX'S DECISION. It is a separate `<tr>`, so its
    // dropdown is not inside this row and would simply never be read - the correction
    // would sit on screen, look applied, and never leave the page. It has no tick of its
    // own precisely because the box carries both, and that has to hold for the edits too.
    const booster = this.boosterRowOf(row);
    if (booster) {
      booster.querySelectorAll('[data-edit]').forEach((input) => {
        edits[input.dataset.edit] = input.value;
      });
    }
    // The same correction against every review row behind this line: an amalgamated line
    // stands for more than one row of the sheet, and all of them are being approved.
    const out = {};
    this.rowIdsOf(row).forEach((rowId) => { out[rowId] = edits; });
    return out;
  }

  /**
   * The review-sheet rows behind one line on screen.
   *
   * A LINE IS NOT ALWAYS A ROW. Several sheet rows are amalgamated into one line where a
   * note repeats a product, so the ids live on the element as a list and every one of them
   * is decided together. Read here rather than inline, because `approveAll` needs the same
   * list to batch by and two copies of that parsing is how the two drift apart.
   */
  rowIdsOf(row) {
    return (row.dataset.rowIds || '').split(',').filter(Boolean);
  }

  /**
   * WHAT THIS PAGE BELIEVES IS ON EACH ROW IT IS ABOUT TO DECIDE.
   *
   * A decision travels as a row NUMBER, and from 12/09/2026 the Split button INSERTS rows,
   * which shifts every row beneath it. So a number this page read a minute ago can now
   * point at a different product - the hazard that came within three minutes of approving
   * the wrong thing on 29/08/2026, when a delete did the shifting.
   *
   * Sending the supplier code alongside turns that from silent damage into a refusal: the
   * server checks the row still carries the code the page drew, and refuses the whole
   * request if it does not. Same shape as `expect` on the guarded Sheets delete and as the
   * till's conflict check before a stock write - state what you expect, and let the far
   * side disagree.
   *
   * A BLANK CODE IS NOT A DISAGREEMENT. Some rows genuinely carry none, and refusing those
   * would block real approvals to prevent a rarer fault - so the server exempts them, and
   * this sends the blank honestly rather than inventing something to compare.
   */
  expectFor(row) {
    const code = row.dataset.code || '';
    const out = {};
    this.rowIdsOf(row).forEach((rowId) => { out[rowId] = code; });
    return out;
  }

  /**
   * ONE DECISION IN FLIGHT AT A TIME, ACROSS THE WHOLE PAGE (10/09/2026).
   *
   * TWO OVERLAPPING APPROVAL RUNS CREATED ONE ROW'S PRODUCT TWICE. Run 39547 started two
   * seconds before 39546 wrote its statuses, read the same review rows as still pending,
   * and made a second till product for each - two Pokemon playmats and a deck holder, all
   * deleted by hand afterwards. The backend decides what to do by RE-READING the review
   * sheet, so a row is only safe from a second run once the first has written its status,
   * and nothing on this page stopped a second run starting inside that window.
   *
   * `approveAll` disabled its OWN button and nothing else, so an individual tick could
   * fire alongside it - and after a failure it re-armed that button against the rows still
   * on screen, which is that window at its widest.
   *
   * A flag on the app rather than a disabled button, because the hazard is two runs
   * writing the same till at once whichever control started them.
   */
  claim() {
    if (this.deciding || this.inFlight.size || this.approving.size) return false;
    this.deciding = true;
    this.setGlobalControls();
    return true;
  }

  release() {
    this.deciding = false;
    this.setGlobalControls();
  }

  /**
   * THE SUPPLIERS BEING APPROVED IN FULL RIGHT NOW, by their box's delivery numbers.
   *
   * APPROVE ALL LOCKS ITS OWN SUPPLIER, NOT THE PAGE (Jonathan, 09/10/2026: "they shouldn't
   * be greyed out just because we approved another delivery"). It used to take the
   * page-wide claim, so every other supplier's ticks stayed grey until the whole list had
   * reloaded. That was the 10/09 guard against two runs creating one row twice - and since
   * 02/10 `Deliveries Ops` leases every review row it acts on, so a tick on another
   * supplier's line cannot touch the rows Approve all is deciding.
   */
  get approving() {
    if (!this.approvingNotes) this.approvingNotes = new Set();
    return this.approvingNotes;
  }

  noteLocked(row) {
    const box = row && row.closest('.deliveries__note');
    return !!(box && this.approving.has(box.dataset.deliveryNo));
  }

  /**
   * AFTER A FAILED DECISION, ASK WHAT ACTUALLY LANDED - BEFORE ANYBODY TRIES AGAIN.
   *
   * A FAILURE HERE DOES NOT MEAN THE WORK DID NOT HAPPEN. Both failures this page has
   * really seen were lost ANSWERS rather than lost work: a 34-line note that took the
   * backend 3m12s and finished perfectly while the browser gave up, and a `Respond
   * Decided` answering `Bad request` on a batch whose products had already been created.
   * So once a batch has failed, the rows on screen are evidence of nothing - and this page
   * used to re-arm its button against exactly those rows and say pressing again was safe.
   *
   * SO KEEP ASKING THE SERVER UNTIL THE ROWS IN DOUBT SETTLE. A run still writing clears
   * them within seconds; one that genuinely failed leaves them pending however long we
   * wait. Either way the list afterwards is what the server actually has, so a retry is
   * built from a fresh read and can never re-send a row that already went through.
   *
   * A row that is no longer pending is out of this page's hands whether it was approved,
   * rejected or filed - which is the only question being asked here.
   */
  async settle(doubted) {
    const deadline = Date.now() + 60000;
    this.lastPending = null;
    const want = doubted.map((id) => String(id));
    let landed = false;
    while (Date.now() < deadline) {
      await new Promise((done) => { setTimeout(done, 4000); });
      let pending = null;
      try {
        const data = await this.call('list', {}, true);
        pending = new Set();
        (data.deliveries || []).forEach((note) => {
          (note.lines || []).forEach((line) => {
            if (line.status === 'pending') pending.add(String(line.rowId));
          });
        });
      } catch (ignored) {
        // A READ THAT FAILED SAYS NOTHING EITHER WAY, so it must not be read as "settled".
        // Treating it as an answer is how a network wobble would license the retry this
        // whole method exists to hold back.
        continue;
      }
      this.lastPending = pending;
      if (!want.some((id) => pending.has(id))) { landed = true; break; }
    }
    // NO RELOAD (Jonathan, 28/10/2026): only the lines in doubt are updated, by the caller, from
    // what this read found; every other line stays exactly as it was.
    return landed;
  }

  /**
   * A `?` IS NOT A PRICE, AND MUST NOT BE APPROVED PAST.
   *
   * The whole point of staging an unpriced line rather than refusing it is that somebody
   * fills the price in. Without this the `?` is worse than the old refusal: the row looks
   * ordinary, the tick works, and a product reaches the till priced at nothing - which is
   * silent, because a zero price is a real number the till will happily accept.
   *
   * IT ASKS THE DOM, NOT THE LINE. `approveAllOnce` works from rows it collected off the
   * page and has no line object to consult, so a guard needing one would protect the
   * single tick and quietly not protect the button that decides a whole note - which is
   * the more dangerous of the two.
   *
   * IT HOLDS BACK A CLEARED RESTOCK PRICE TOO, since 10/09/2026, which is the whole of
   * what changed when the field stopped being create-only. A restock arrives carrying the
   * till's own price, so it can only be blank because somebody emptied it - and sending
   * that would ask the till to wipe a real price rather than leave it alone.
   *
   * The booster is included because it is created with its box, and its fields ride on the
   * box's decision - so a box with a priced booster and a `?` on itself, or the reverse,
   * must both stop here.
   */
  unpricedIn(row) {
    const missing = [];
    const check = (scope, what) => {
      if (!scope) return;
      const field = scope.querySelector('[data-edit="' + (what.key) + '"]');
      // A field that is not on the row at all cannot be judged - say nothing rather than
      // inventing a fault, which is how a guard starts blocking work it was never about.
      if (field && String(field.value).trim() === '') missing.push(what.label);
    };
    const name = row.querySelector('.deliveries__name-text');
    check(row, { key: 'salePrice', label: (name && name.textContent.trim()) || 'this product' });
    check(this.boosterRowOf(row), { key: 'childSalePrice', label: 'its loose booster' });
    return missing.length ? missing : null;
  }

  async decide(row, line, operation) {
    if (operation === 'approve') {
      const missing = this.unpricedIn(row);
      if (missing) {
        this.say('No price for ' + missing.join(' or ') + '. Type one in first - tap the '
          + 'price to edit it.', true);
        return;
      }
    }
    // ONE LINE AT A TIME PER LINE, NOT PER PAGE (Jonathan, 02/10/2026): *"allow it to create
    // preorders from other lines while one is working ... I want to be able to create
    // several at once."* The page-wide lock of 10/09 is no longer what stops a double
    // create - `Deliveries Ops` leases every review row it acts on (`claimRows`), so two runs
    // can never both act on one row. What stays exclusive is Approve all and Split, which
    // decide or rewrite many rows and reload the list; they wait for every line to finish.
    if (this.deciding || this.noteLocked(row)) {
      this.say('Something else on this page is still saving. Wait for it to finish.', true);
      return;
    }
    if (this.inFlight.has(row)) return;
    this.inFlight.add(row);
    this.rowBusy(row, true);
    this.setGlobalControls();
    try {
      await this.decideOnce(row, line, operation);
    } finally {
      this.inFlight.delete(row);
      this.rowBusy(row, false);
      this.setGlobalControls();
      if (!this.inFlight.size && this.reloadWanted) {
        this.reloadWanted = false;
        this.load();
      }
    }
  }

  get inFlight() {
    if (!this.inFlightRows) this.inFlightRows = new Set();
    return this.inFlightRows;
  }

  /** A spinner beside the line's own buttons, which are off while it saves. */
  rowBusy(row, on) {
    const actions = row.querySelector('.deliveries__actions');
    if (!actions) return;
    if (on) {
      row.classList.add('is-saving');
      actions.querySelectorAll('button').forEach((b) => { b.disabled = true; });
      if (!actions.querySelector('.deliveries__row-spinner')) {
        const spinner = document.createElement('span');
        spinner.className = 'deliveries__spinner deliveries__row-spinner';
        spinner.setAttribute('aria-label', 'Saving');
        actions.prepend(spinner);
      }
      return;
    }
    row.classList.remove('is-saving');
    const spinner = actions.querySelector('.deliveries__row-spinner');
    if (spinner) spinner.remove();
    // A DECIDED ROW HAS NO BUTTONS LEFT TO RE-ARM; an undecided one (a failed save) gets its
    // own back, minus anything off because of what the row IS.
    actions.querySelectorAll('button').forEach((b) => { b.disabled = b.dataset.alwaysOff === 'yes'; });
  }

  /**
   * Every control's on/off, from three facts: a Split in progress (the whole page), a
   * supplier being approved in full (that supplier only), and lines saving (their own
   * supplier's Approve all, and every Split, since a split reloads the list).
   *
   * `data-always-off` IS NEVER RE-ARMED - a control off because of what its row IS (the
   * split button on an amalgamated line) stays off whatever else changes.
   */
  setGlobalControls() {
    const anyBusy = this.deciding || this.inFlight.size > 0 || this.approving.size > 0;
    this.querySelectorAll('.deliveries__note').forEach((box) => {
      const locked = this.deciding || this.approving.has(box.dataset.deliveryNo);
      const saving = [...this.inFlight].some((row) => box.contains(row));
      const all = box.querySelector('.deliveries__approve-all');
      // A note whose every line is decided has nothing left for Approve all (28/10/2026).
      const finished = ![...box.querySelectorAll('.deliveries__row')].some((r) => !r.dataset.retiring);
      if (all) all.disabled = locked || saving || finished;
      box.querySelectorAll('.deliveries__actions button').forEach((b) => {
        if (this.inFlight.has(b.closest('.deliveries__row'))) return;
        const isSplit = b.classList.contains('deliveries__split');
        b.disabled = b.dataset.alwaysOff === 'yes' || locked || (isSplit && anyBusy);
      });
    });
  }

  /** Reload the list - but never under lines still saving; do it when the last finishes. */
  loadWhenQuiet() {
    if (this.inFlight.size) { this.reloadWanted = true; return Promise.resolve(); }
    return this.load();
  }

  async decideOnce(row, line, operation) {
    try {
      const answer = await this.call(operation, {
        rowIds: line.rowIds || [line.rowId],
        edits: operation === 'approve' ? this.editsFor(row) : {},
        // SENT ON A REJECTION TOO, and that is deliberate: rejecting the wrong row is just
        // as wrong as approving it, and a reject carries no edits to check it against.
        expect: this.expectFor(row),
      });
      if (answer && answer.error) throw new Error(answer.error);
      // THE ROW STAYS ON SCREEN, marked, for the same reason the stock page's does: a line
      // vanishing under somebody working down a list is how you lose your place.
      this.markDecided(row, operation);
      this.afterDecision(answer);
    } catch (err) {
      // THE BUTTONS ARE NOT SIMPLY RE-ARMED. That is what made one click re-send a line
      // whose first attempt may already have reached the till.
      this.say(`That did not save: ${err.message}. Checking what reached the till…`, true);
      const landed = await this.settle(line.rowIds || [line.rowId]);
      // ONLY THIS LINE IS UPDATED (28/10/2026): marked as decided when it went through, and
      // otherwise left waiting, its buttons back on once the save is over.
      if (landed) this.markDecided(row, operation);
      // Nothing could be read at all: fall back to the whole list, so a line that did go
      // through is never left looking pressable.
      else if (!this.lastPending) await this.loadWhenQuiet();
      this.say(landed
        ? `${err.message} — but that line did reach the till after all, so it is marked as done.`
        : `That did not save: ${err.message}. That line is still waiting; try it again.`, true);
    }
  }

  /**
   * EVERY EDIT ON THE PAGE, NOT JUST THE ROW BEING ACTED ON.
   *
   * Jonathan, 12/09/2026: *"It's important when using this split button that we don't lose
   * any of the other changes that the user may have made on the page."*
   *
   * A split RELOADS - the new rows only exist once the server has written them, and their
   * row numbers come back with them - and a reload replaces every field on screen with
   * whatever the sheet holds. So anything typed into a row nobody has approved yet would
   * be thrown away by the very act of splitting a different line. Sending the lot means
   * the server writes them all back first, and the reload then reads them.
   *
   * A row that has already been decided is skipped: its edits were sent with the decision,
   * and re-sending them would rewrite a row that is no longer pending. A booster row is
   * skipped because its fields are collected by its BOX - visiting it here would key its
   * edits on nothing, which is the same reason `approveAll` skips it.
   */
  allEdits() {
    const out = {};
    [...this.querySelectorAll('.deliveries__row')].filter((row) => (
      !row.classList.contains('is-approved')
      && !row.classList.contains('is-rejected')
      && !row.classList.contains('deliveries__row--child')
    )).forEach((row) => { Object.assign(out, this.editsFor(row)); });
    return out;
  }

  /**
   * THE ONLY COUNTS A QUANTITY CAN BE SPLIT INTO (Jonathan, 12/09/2026): *"Can you only
   * allow splitting if the quantity is appropriately divisible? Eg., if the quantity is 6,
   * the only allowable values should be 2 and 3."*
   *
   * THE TOTAL MUST STILL MATCH THE DELIVERY NOTE, which is what makes this the right rule
   * rather than a fussy one: six decks split in two is three and three, and there is no
   * honest way to split six into four. Offering only divisors means every piece gets the
   * same whole number and nothing has to round.
   *
   * THE QUANTITY ITSELF IS OFFERED, which is the one place this goes past his example - he
   * listed 2 and 3 for six, not 6. Excluding it would make a quantity of TWO unsplittable
   * altogether, since its only divisor is two, and a two-pack holding two different
   * products is the commonest case this feature is for.
   */
  /**
   * IS THIS LINE A BOOSTER BOX? Three tells, because no single one catches all of them.
   *
   * `caseSize` is the honest identity - it is what makes the created till product a master,
   * carrying sealed boxes and loose boosters on one record. A visible booster row, or a
   * visible reason there is not one, says the same thing from the other direction: a box
   * whose booster could NOT be worked out still IS a box, and testing only for a derived
   * booster would have let exactly that one through.
   *
   * The server checks `caseSize` itself and is the authority. This is here so the button is
   * off with its reason showing, rather than opening a dialogue that is refused afterwards.
   */
  isBoosterBox(line) {
    const caseSize = Number(line.caseSize);
    return (Number.isFinite(caseSize) && caseSize > 1)
      || Boolean(line.child)
      || Boolean(String(line.childRefusal || '').trim());
  }

  splitOptions(units) {
    const total = Number(units) || 0;
    const out = [];
    // TWELVE IS THE CEILING, and the server refuses past it too. The pieces are lettered
    // A, B, C, so an unbounded count runs off the end of the alphabet and starts naming
    // rows after punctuation - and nothing the shop receives is one line holding more than
    // a dozen different products.
    for (let n = 2; n <= total && n <= 12; n += 1) if (total % n === 0) out.push(n);
    return out;
  }

  /**
   * THE DIALOGUE, BUILT RATHER THAN NATIVE. `prompt()` cannot enforce a minimum, returns a
   * string, and reads as a fault on a tablet - which is exactly how the old paste-a-link
   * prompt was reported on 08/09/2026 before it was removed.
   *
   * It states what the split will DO to the quantity before anything is sent, because that
   * is the half of this that is not obvious and the half that reaches the till.
   */
  askSplitCount(row, line) {
    return new Promise((resolve) => {
      const nameOf = row.querySelector('.deliveries__name-text');
      const name = (nameOf && nameOf.textContent.trim()) || 'this line';

      const back = document.createElement('div');
      back.className = 'deliveries__dialog-back';
      const box = document.createElement('div');
      box.className = 'deliveries__dialog';
      box.setAttribute('role', 'dialog');
      box.setAttribute('aria-modal', 'true');

      // NO HEADING (Jonathan, 12/09/2026: "no title"). The product name below is what the
      // reader needs, and "Split into separate products" said what the button they had just
      // pressed already said.
      const what = document.createElement('p');
      what.className = 'deliveries__dialog-what';
      what.textContent = name;

      // A DROPDOWN, NOT A NUMBER BOX (his suggestion, and it is the right one). The valid
      // answers are a short fixed list - the divisors of the quantity - so a free number
      // field would exist mainly to reject what was typed into it.
      const label = document.createElement('label');
      label.className = 'deliveries__dialog-label';
      label.textContent = 'How many products do you want to split this into?';
      const field = document.createElement('select');
      field.className = 'deliveries__dialog-number';
      const units = Number(line.units) || 0;
      this.splitOptions(units).forEach((n) => {
        const option = document.createElement('option');
        option.value = String(n);
        // THE NUMBER ALONE (Jonathan, 12/09/2026: "Remove the text in the brackets"). Each
        // option used to carry its share - "3  (2 each)".
        option.textContent = String(n);
        field.appendChild(option);
      });
      label.appendChild(field);

      // NO RUNNING COMMENTARY (his instruction, same message): the dialogue says the one
      // question and nothing else. It used to spell out the names each piece would get and
      // how the quantity divided, which is knowable from the number in the field and is not
      // worth four lines of prose in front of a person holding a box.
      const count = () => Number(field.value) || 2;

      const buttons = document.createElement('div');
      buttons.className = 'deliveries__dialog-buttons';
      const cancel = document.createElement('button');
      cancel.type = 'button';
      cancel.className = 'deliveries__dialog-cancel';
      cancel.textContent = 'Cancel';
      const ok = document.createElement('button');
      ok.type = 'button';
      ok.className = 'deliveries__dialog-ok';
      ok.textContent = 'Split';
      buttons.appendChild(cancel);
      buttons.appendChild(ok);

      let done = false;
      const close = (answer) => {
        if (done) return;
        done = true;
        document.removeEventListener('keydown', onKey);
        back.remove();
        resolve(answer);
      };
      const onKey = (event) => {
        if (event.key === 'Escape') { event.preventDefault(); close(null); }
        if (event.key === 'Enter') { event.preventDefault(); close(count()); }
      };
      cancel.addEventListener('click', () => close(null));
      ok.addEventListener('click', () => close(count()));
      // Clicking the backdrop cancels; clicking the dialog itself must not.
      back.addEventListener('click', (event) => { if (event.target === back) close(null); });
      document.addEventListener('keydown', onKey);

      box.appendChild(what);
      box.appendChild(label);
      box.appendChild(buttons);
      back.appendChild(box);
      document.body.appendChild(back);
      // FOCUS ONLY. `field.select()` used to follow this line, left over from when the
      // field was a number `<input>` - and `HTMLSelectElement` HAS NO `select()`, so it
      // threw a TypeError, rejected this promise, and `split()` never reached the fetch.
      //
      // IT LOOKED EXACTLY LIKE NOTHING HAPPENING: the dialogue is appended one line above,
      // so it opened, took the answer, closed on the click - and the rejected promise meant
      // the resolve was a no-op. No request was ever made and no message was ever shown.
      // Jonathan reported it as "I tried to split this into two, and nothing happened", and
      // the backend confirmed it: not one split request had arrived.
      field.focus();
    });
  }

  async split(row, line) {
    // THE DIALOGUE IS INSIDE THE TRY, because a throw ANYWHERE before the fetch used to be
    // invisible - `split` is a click handler, so a rejected promise goes nowhere and the
    // button reads as dead. That is precisely how the `field.select()` fault hid.
    let count = null;
    try {
      count = await this.askSplitCount(row, line);
    } catch (err) {
      this.say(`The split dialogue could not be opened: ${err.message}`, true);
      throw err;
    }
    if (!count) return;
    // The same one-at-a-time claim the tick and cross take. A split rewrites the very rows
    // an approval would be deciding, so the two must never overlap.
    if (!this.claim()) {
      // SAYING SO, rather than returning in silence. A refused claim is the one other way
      // this button can do nothing at all, and a person cannot tell it from a fault.
      this.say('Something else on this page is still saving. Wait for it to finish and '
        + 'press Split again.', true);
      return;
    }
    try {
      this.say('Splitting…');
      // THE LINE SPINS WHILE IT SPLITS (Jonathan, 24/10/2026) - the same spinner a saving line shows.
      this.rowBusy(row, true);
      const answer = await this.call('split', {
        rowIds: line.rowIds || [line.rowId],
        count,
        edits: this.allEdits(),
        expect: this.expectFor(row),
      });
      if (answer && answer.error) throw new Error(answer.error);
      // ONLY THE SPLIT LINE IS REDRAWN (Jonathan, 28/10/2026): every other line - greyed decided
      // ones, typed corrections, open pictures - stays exactly as it was. A full reload is the
      // fallback only when the pieces cannot be found.
      const swapped = await this.replaceSplitRow(row, line);
      if (!swapped) await this.load();
      this.say(`Split into ${count}. Rename each one, then approve them.`);
    } catch (err) {
      // NO RELOAD ON A REFUSAL, deliberately. A refused split has changed nothing that is
      // not already on screen, and reloading would throw away everything typed into every
      // other row - the exact loss this whole feature is written to avoid.
      this.say(`That did not split: ${err.message}. Nothing on screen has been lost.`, true);
    } finally {
      // After a successful split the reload has already replaced this row; this clears a refused one.
      if (row.isConnected) this.rowBusy(row, false);
      this.release();
    }
  }

  /**
   * Put the pieces of a split in place of the line that was split, and touch nothing else.
   *
   * The pieces are the waiting lines on the server that no line on screen already stands for
   * (each on-screen line knows its review rows), plus whatever now carries the split line's own
   * rows. Returns false, and changes nothing, when none can be found - the caller then reloads.
   */
  async replaceSplitRow(row, line) {
    const data = await this.call('list', {});
    this.categories = data.categories || this.categories;
    // ONLY THE SPLIT LINE'S OWN NOTES are searched, so a line staged meanwhile on another
    // delivery is never slotted in where the split was; it appears on the next refresh.
    const box = row.closest('.deliveries__note');
    const own = new Set(String((box && box.dataset.deliveryNo) || '').split(',').filter(Boolean));
    const notes = this.forThisMode(data.deliveries || []).filter((n) => !own.size || own.has(String(n.deliveryNo)));
    const mine = new Set((line.rowIds || [line.rowId]).map(String));
    const shown = new Set();
    this.querySelectorAll('.deliveries__row').forEach((r) => {
      if (r === row) return;
      String(r.dataset.rowIds || '').split(',').filter(Boolean).forEach((id) => shown.add(id));
    });
    const pieces = [];
    for (const note of notes) {
      const pending = this.amalgamate((note.lines || []).filter((l) => l.status === 'pending'));
      for (const l of pending) {
        const ids = (l.rowIds || [l.rowId]).map(String);
        if (ids.some((id) => mine.has(id)) || !ids.some((id) => shown.has(id))) pieces.push(l);
      }
    }
    if (!pieces.length || !row.parentNode) return false;
    const after = [];
    const booster = this.boosterRowOf(row);
    if (booster) after.push(booster);
    if (row.priorRow) after.push(row.priorRow);
    const body = row.parentNode;
    const anchor = (after.length ? after[after.length - 1] : row).nextSibling;
    [row, ...after].forEach((el) => el.remove());
    for (const l of pieces) {
      const own = this.line(l);
      body.insertBefore(own, anchor);
      const extra = this.boosterRow(l);
      if (extra) body.insertBefore(extra, anchor);
      const prior = this.priorRow(l);
      if (prior) { body.insertBefore(prior, anchor); own.priorRow = prior; }
    }
    this.setGlobalControls();
    this.fillMissingPictures(notes.map((n) => Object.assign({}, n, {
      lines: (n.lines || []).filter((l) => pieces.some((p) => (p.rowIds || [p.rowId]).includes(l.rowId))),
    })));
    return true;
  }

  /**
   * A FEW LINES PER REQUEST, NOT THE WHOLE NOTE (Jonathan, 07/09/2026 - "it failed").
   *
   * IT HAD NOT FAILED. A 34-line heo note took the backend THREE MINUTES TWELVE SECONDS and
   * finished perfectly; the browser gave up long before, and `fetch` rejecting is all
   * "Failed to fetch" ever meant. Every line was on the till, the page said the opposite,
   * and the obvious response to that message is to press the button again.
   *
   * SO THE FIX IS TO MAKE NO SINGLE REQUEST LONG ENOUGH TO BE ABANDONED. Approving is
   * roughly five seconds a line - it creates products, writes stock and fetches artwork -
   * so a whole note is unbounded while a handful of lines is not. The total takes just as
   * long; what changes is that it arrives as a series of answers instead of one silence.
   *
   * `approve` RATHER THAN `approveAll`, and the note is still filed. The backend decides
   * what to file by RE-READING the review sheet and asking whether a note has any line left
   * undecided - so the last batch completes the note and files its email exactly as one big
   * call would have. Checked against `Which To File` before relying on it.
   *
   * SEQUENTIAL, because the batches write the same till and the 15/08 lesson is that
   * concurrent writes to one record race with the loser silently overwritten.
   *
   * A BATCH WHOSE ANSWER IS LOST IS CHECKED, THEN THE RUN CARRIES ON (28/09/2026). The
   * server is asked whether those lines went through; if they did, the rest are sent with the
   * corrections captured before the first batch, and if they did not the run stops and says
   * so - the rows behind it untouched and still pending, so pressing again resumes from there.
   */
  /** This note's Approve all button as the page has it NOW - a reload replaces it. */
  approveAllButton(box) {
    const no = box && box.dataset ? box.dataset.deliveryNo : '';
    const live = [...this.querySelectorAll('.deliveries__note')].find((b) => b.dataset.deliveryNo === no);
    return live ? live.querySelector('.deliveries__approve-all') : null;
  }

  async approveAll(box, note) {
    const key = box.dataset.deliveryNo;
    if (this.deciding || this.approving.has(key)
      || [...this.inFlight].some((row) => box.contains(row))) {
      this.say('Something on this delivery is still saving. Wait for it to finish.', true);
      return;
    }
    this.approving.add(key);
    this.setGlobalControls();
    try {
      await this.approveAllOnce(box, note);
    } finally {
      this.approving.delete(key);
      this.setGlobalControls();
    }
  }

  async approveAllOnce(box, note) {
    const button = box.querySelector('.deliveries__approve-all');

    // Every correction still counts. Approve all is a shortcut past clicking each tick,
    // not past what is on screen - somebody who fixed a category and then pressed it must
    // get the fixed category. A booster row's fields are collected by its BOX, which is the
    // row that carries the review row ids; visiting it here would key its edits on nothing.
    const rows = [...box.querySelectorAll('.deliveries__row')].filter((row) => (
      !row.classList.contains('is-approved')
      && !row.classList.contains('is-rejected')
      && !row.classList.contains('deliveries__row--child')
    ));
    const total = rows.length;
    if (!total) { button.textContent = 'Added'; return; }

    // NOTHING IS SENT WHILE ANY ROW STILL SAYS `?`. Checked across the WHOLE note before
    // the first batch rather than per batch: batches of four are sent in sequence, so a
    // row failing this in batch three would leave the first eight already on the till and
    // the note half added - which is the state the claim work this morning exists to
    // avoid. Refusing up front costs nothing and leaves the note exactly as it was.
    const unpriced = rows.reduce((all, row) => all.concat(this.unpricedIn(row) || []), []);
    if (unpriced.length) {
      this.say(`${unpriced.length} line${unpriced.length === 1 ? '' : 's'} on this note `
        + `still need${unpriced.length === 1 ? 's' : ''} a price: ${unpriced.join(', ')}. `
        + 'Tap the ? to type one in.', true);
      return;
    }

    const BATCH = 4;
    let done = 0;
    let answer = null;
    let failure = '';
    let doubted = [];
    // LINES THE SERVER REFUSED WHILE THE REST OF THEIR BATCH WENT THROUGH (29/09/2026). One
    // line that cannot be created - "Sky Team" with no VAT rate - used to sink every line
    // sent with it. `partial: true` asks the server to add the others and name the refusals.
    const refusals = [];

    // EVERY LINE'S IDS, CORRECTIONS AND EXPECTED CODE, TAKEN BEFORE ANYTHING IS SENT
    // (28/09/2026). A lost answer makes the page reload from the server, which rebuilds the
    // rows and throws away anything typed on a line not yet sent - so carrying on from the
    // fresh rows would approve that line at its original price or name. These captured
    // values are what every batch sends, before and after any reload.
    let units = rows.map((row) => ({
      row, ids: this.rowIdsOf(row), edits: this.editsFor(row), expect: this.expectFor(row),
    }));
    // A LOST ANSWER WHOSE WORK LANDED IS NOT A REASON TO STOP (Jonathan, 28/09/2026: the
    // worklist's "Approve all times the browser out, and it looks like a failure"). The batch
    // in doubt is settled against the server exactly as before; if it went through, the rest
    // carry on by themselves instead of the page ending on "That did not save". Three lost
    // answers in one run stop it, so a server that has stopped answering is not hammered.
    let lostAnswers = 0;
    let lastLanded = false;

    while (units.length) {
      const slice = units.slice(0, BATCH);
      const rowIds = [];
      const edits = {};
      const expect = {};
      slice.forEach((unit) => {
        rowIds.push(...unit.ids);
        Object.assign(edits, unit.edits);
        Object.assign(expect, unit.expect);
      });
      // HOW MANY HAVE ACTUALLY BEEN ADDED, not how many are being tried (Jonathan, 14/09/2026):
      // it opens at 0 of 6 and moves only when a batch has landed.
      { const b = this.approveAllButton(box); if (b) b.textContent = `Adding (${done} of ${total})`; }
      this.say(`Adding the delivery to the till — ${done} of ${total} lines so far.`);
      try {
        const got = await this.call('approve', { rowIds, edits, expect, partial: true });
        if (got && got.error) throw new Error(got.error);
        answer = got;
        const refusedIds = new Set(((got && got.refused) || []).map((r) => String(r.rowId)));
        ((got && got.refused) || []).forEach((r) => refusals.push(r.error || String(r.name)));
        const refusedUnits = slice.filter((unit) => unit.ids.some((id) => refusedIds.has(String(id))));
        slice.forEach((unit) => {
          if (!refusedUnits.includes(unit)) this.markAddedInPlace(unit.row);
        });
        done += slice.length - refusedUnits.length;
        units = units.slice(slice.length);
        { const b = this.approveAllButton(box); if (b) b.textContent = `Adding (${done} of ${total})`; }
      } catch (err) {
        // The rows this batch asked for, which are now in doubt rather than known-pending.
        doubted = rowIds;
        lostAnswers += 1;
        this.say(`No answer yet for ${slice.length} line${slice.length === 1 ? '' : 's'} — `
          + 'checking whether they reached the till…');
        // ONE CHECK, WHICHEVER WAY IT GOES: the ending below reuses this answer rather
        // than waiting another minute to ask again.
        lastLanded = await this.settle(doubted);
        if (!lastLanded || lostAnswers > 3) { failure = err.message; break; }
        slice.forEach((unit) => this.markAddedInPlace(unit.row));
        done += slice.length;
        // What is still pending on the server, from the read that settled the batch. A line
        // somebody else decided meanwhile drops out rather than being sent again.
        const pending = this.lastPending || new Set();
        units = units.slice(slice.length)
          .filter((unit) => unit.ids.every((id) => pending.has(String(id))));
        const fresh = this.approveAllButton(box);
        if (fresh) fresh.textContent = `Adding (${done} of ${total})`;
      }
    }

    if (!failure) {
      const fresh = this.approveAllButton(box);
      if (fresh) fresh.textContent = 'Added';
      // THE LINES STAY ON THE PAGE, GREYED, JUST AS TICKING EACH ONE LEAVES THEM (Jonathan,
      // 28/10/2026, replacing the 07/09 reload). A refused line is still waiting, so it is left
      // live where it is and named in the message; a refresh shows only what is still waiting.
      if (done && answer) this.afterDecision(answer);
      if (refusals.length) {
        const again = this.approveAllButton(box);
        if (again) { again.textContent = 'Approve all'; again.disabled = false; }
        this.say(`${done} of ${total} line${total === 1 ? '' : 's'} added to the till. `
          + `Not added: ${refusals.join(' ')}`, true);
        return;
      }
      // NO POP-UP WHEN IT WORKED (Jonathan, 28/10/2026): every line is greyed in place.
      this.say('');
      return;
    }

    const again = this.approveAllButton(box);
    if (again) again.textContent = 'Approve all';
    // SAY HOW FAR IT GOT, AND THEN GO AND FIND OUT ABOUT THE REST.
    //
    // THIS USED TO RE-ARM THE BUTTON AND SAY "press again for the rest", on the reasoning
    // that "a row already approved is no longer pending and cannot be applied twice". That
    // is true only once the failed run has WRITTEN its statuses - and a failure is exactly
    // the case where we do not know whether it has. Pressing again inside that window is
    // what created two Pokemon playmats and a deck holder twice over on 09/09/2026.
    //
    // So the batch in doubt is settled against the server first, and this note's lines are
    // marked from what it found (28/10/2026, in place of a full reload): a line no longer waiting
    // shows as added, one still waiting stays live. So the next press picks up from there and
    // cannot re-send anything that landed, and every other line on the page is left alone.
    if (this.lastPending) {
      const live = this.approveAllButton(box);
      const noteBox = live ? live.closest('.deliveries__note') : box;
      [...noteBox.querySelectorAll('.deliveries__row')].filter((r) => !r.dataset.retiring
        && !r.classList.contains('deliveries__row--child')).forEach((r) => {
        const ids = this.rowIdsOf(r).map(String);
        if (ids.length && ids.every((id) => !this.lastPending.has(id))) this.markAddedInPlace(r);
      });
    } else {
      // THE CHECK COULD NOT READ THE LIST AT ALL, so nothing is known about which lines landed:
      // the whole list is read again from the server rather than leaving a line that went
      // through looking pressable.
      await this.loadWhenQuiet();
    }
    if (done && answer) this.afterDecision(answer);
    // Already checked in the loop; stopping after three lost answers is the only way to
    // arrive here with that batch landed.
    const landed = lastLanded;
    const how = done ? ` ${done} of ${total} lines had already gone through.` : '';
    this.say(landed
      ? `${failure} — but that batch did reach the till after all.${how} `
        + 'Lines still waiting are left as they are, so Approve all picks up from there.'
      : `That did not save: ${failure}.${how} Those lines are still waiting, `
        + 'so Approve all picks up from there.', true);
  }

  /**
   * A DECIDED ROW STAYS ON THE PAGE, GREYED, UNTIL THE PAGE IS REFRESHED (Jonathan, 28/10/2026).
   *
   * It used to fade out after ten seconds (15/08/2026), and the note with its last row. Now it keeps
   * its place, still tinted and labelled with what happened to it, so the list never shifts under
   * somebody working down it; a refresh shows only what is still waiting.
   *
   * A NOTE WHOSE EVERY LINE IS DECIDED greys its Approve all button, because there is nothing left
   * for it to do - a live button over a finished list reads as something still waiting.
   */
  /** A decided line: tinted, labelled, its booster with it, and greyed in place until refresh. */
  markDecided(row, operation) {
    row.classList.add(operation === 'approve' ? 'is-approved' : 'is-rejected');
    const actions = row.querySelector('.deliveries__actions');
    if (actions) {
      actions.innerHTML = `<span class="deliveries__done">${operation === 'approve'
        ? (this.preorderMode() ? 'Created' : 'Added')
        : (this.preorderMode() ? 'Removed' : 'Rejected')}</span>`;
    }
    // THE BOOSTER GOES WITH ITS BOX. It has no tick of its own, so nothing else would ever
    // mark it, and the note would never read as finished.
    const booster = this.boosterRowOf(row);
    if (booster) {
      booster.classList.add(operation === 'approve' ? 'is-approved' : 'is-rejected');
      this.retireRow(booster);
    }
    this.retireRow(row);
  }

  /** A line Approve all has added: marked exactly as its own tick would mark it, and greyed in place. */
  markAddedInPlace(row) {
    row.classList.add('is-approved');
    const actions = row.querySelector('.deliveries__actions');
    if (actions) actions.innerHTML = `<span class="deliveries__done">${this.preorderMode() ? 'Created' : 'Added'}</span>`;
    const booster = this.boosterRowOf(row);
    if (booster) { booster.classList.add('is-approved'); this.retireRow(booster); }
    this.retireRow(row);
  }

  retireRow(row) {
    if (row.dataset.retiring) return;
    row.dataset.retiring = '1';
    row.classList.add('is-dealt');
    if (row.priorRow) row.priorRow.classList.add('is-dealt');
    const box = row.closest('.deliveries__note');
    if (box && ![...box.querySelectorAll('.deliveries__row')].some((r) => !r.dataset.retiring)) {
      const all = box.querySelector('.deliveries__approve-all');
      if (all) all.disabled = true;
    }
  }

  afterDecision(answer) {
    // TELL THE STOCK PROCESSING TAB (Jonathan, 15/08/2026). Approving is what puts a
    // product on order, and the Stock processing tab is the list of what is on order -
    // but the tabs only hide and show, so that list was whatever it read when the page
    // first loaded. Approve a delivery, switch across, and the thing you had just
    // approved was not there; the only way to see it was to reload the page.
    //
    // Announced on `document` rather than called directly because the two apps are in
    // separate scopes on purpose - four theme apps sharing one scope is what left five
    // tabs spinning earlier today. An event crosses that boundary without reintroducing
    // the coupling. Nothing listening is not an error: a page showing only Deliveries
    // fires into empty air, which is correct.
    document.dispatchEvent(new CustomEvent('dc:stock-on-order-changed',
      { detail: { preorder: this.preorderMode() } }));

    // A BOOSTER THAT DID NOT MAKE IT IS SAID FIRST, ahead of the good news.
    //
    // Creating a booster and linking it to its box deliberately do NOT stop the run: by
    // the time either happens the BOX is already a real till product with its review row
    // still pending, so a failure that killed the run would leave that product behind an
    // undecided row - one click from being created twice, which is worse than a set
    // missing its booster. The cost of that choice is that this is the only place anyone
    // finds out, so it must not be buried under "Done."
    const subFailures = (answer && answer.subFailures) || [];
    if (subFailures.length) {
      const names = subFailures.map((s) => `${s.name}: ${s.detail}`).join('; ');
      this.say(`The boxes were added, but ${subFailures.length === 1 ? 'a loose booster was'
        : `${subFailures.length} loose boosters were`} not: ${names}. `
        + 'Create it on the till by hand, under its box.', true);
      return;
    }

    const filed = (answer && answer.filed) || [];
    // A REFUSED LINE IS SAID FIRST, AND IT USED TO BE UNREACHABLE. This test sat BELOW
    // the `filed` one, which returns - so the moment a note completed, "did not reach the
    // till" could never be shown. That is precisely when it matters: the email is filed in
    // the same breath, so the line stops being anybody's job. On 08/09/2026 this page said
    // "Done. Delivery 1792558 is finished" about a delivery that had lost two lines, and
    // they were found two days later by tracing one product by hand.
    if (answer && answer.failed) {
      this.say(`${answer.applied} added, but ${answer.failed} did not reach the till.`
        + (filed.length ? ` Delivery ${filed.join(', ')} has still been filed, so those `
          + 'lines will not come back on their own - they are on the manager\'s Problems '
          + 'list.' : ''), true);
      return;
    }
    if (this.preorderMode()) {
      this.say('Created on the till. Its website listing appears within half an hour, and it '
        + 'becomes a pre-order - and goes live if it has a picture - within the half hour after that.');
      return;
    }
    // A FINISHED NOTE WHOSE EMAIL COULD NOT BE FILED IS SAID, NOT SWALLOWED. Deliveries Ops
    // marks those with `mustFileByHand`; the other reasons it skips a note (lines still
    // undecided, Piri Piri, a pre-order) are ordinary and stay quiet. Until 27/09/2026 the
    // page never read this list, so heo note LS88339C sat unfiled with nobody told.
    const unfiled = ((answer && answer.notFiled) || []).filter((n) => n && n.mustFileByHand);
    if (unfiled.length) {
      const which = unfiled.map((n) => `${n.deliveryNo} (${n.why})`).join('; ');
      this.say(`Delivery ${which} is finished, but its email could not be filed. `
        + 'File it by hand - it is on the manager\'s Problems list.', true);
      return;
    }
    if (filed.length) {
      this.say(`Done. Delivery ${filed.join(', ')} is finished, so its email has been filed.`);
      return;
    }
    // NO POP-UP WHEN IT WORKED (Jonathan, 28/10/2026): the decided line stays greyed.
    this.say('');
  }
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}

customElements.define('deliveries-app', DeliveriesApp);
})();
